// pages/admin/BulkTransferPage.tsx
// โอนเงินหลายรายการพร้อมกัน (Bulk Transfer Auto)

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import socketService from '../../services/socketService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup, faFilter, faRotate, faTimes, faUniversity,
  faSpinner, faSearch, faCheckCircle, faTimesCircle, faCalendarDay,
  faMoneyBillWave, faArrowRightArrowLeft, faInfoCircle, faCheck,
  faExclamationTriangle, faHistory, faEye, faChevronDown, faChevronUp,
  faQuestionCircle, faRobot, faPlay, faClock, faBan, faUnlock, faSave, faDownload, faPowerOff
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Pagination } from '../../components/shared/DataTable';
import type { Column } from '../../components/shared/DataTable';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import { getBankCode, generateScbFile, generateScbFileAll, downloadTextFile } from '../../utils/scbExport';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  UiToggle,
  StatusBadge,
  UiInput,
  UiSelect,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const SEARCH_INPUT_CLASS =
  'w-full pl-9 pr-8 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-500 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TABLE_ACTION_BTN =
  'px-2.5 py-1.5 text-[11px] rounded-lg border transition-colors disabled:opacity-50';
const HEADER_GLASS_BTN =
  'px-3 py-1.5 text-xs font-semibold rounded-lg border border-white/50 bg-white/25 hover:bg-white/40 text-white shadow-sm transition-colors';
const LABEL_CLASS =
  'block text-sm font-medium text-gray-700 dark:text-slate-300';

interface Payout {
  id: string;
  payout_number: string;
  amount: number;
  fee_amount: number;
  net_amount: number;
  status: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  created_at: string;
  transfer_response?: any;
  merchant: {
    id: string;
    name: string;
    email: string;
  };
}

interface BulkResultItem {
  payout_id: string;
  payout_number: string;
  bank_account_number: string;
  bank_account_name: string;
  bank_name: string;
  amount: number;
  success: boolean;
  status: string;
  payeeAccountNo?: string;
  payeeAccountName?: string;
}

interface BulkOrder {
  bulk_order_id: string;
  package_ref_no: string;
  bulk_status: string;
  from_account: string;
  banks_id: string;
  approved_by_name: string;
  completed_at: string;
  total_amount: number;
  item_count: number;
  items: {
    payout_id: string;
    payout_number: string;
    bank_account_name: string;
    bank_account_number: string;
    bank_name: string;
    amount: number;
    status: string;
  }[];
}

interface BulkStatusItem {
  bulkItemId: string;
  payeeAccountNo: string;
  payeeAccountName: string;
  payeeBankCode: string;
  payeeBankName: string;
  amount: number;
  status: string;
}

interface BulkStatusResult {
  bulk_order_id: string;
  package_ref_no: string;
  bulk_status: string;
  from_account: string | null;
  total_amount: number;
  total_fee: number;
  grand_total_amount: number;
  items: BulkStatusItem[];
}

interface OrderBulkTransaction {
  transactionDateTime: string | null;
  transactionCode: string | null;
  transactionComment: string | null;
  chequeNumber: string | null;
  transferAmount: number | null;
  taxAmount: number | null;
  balance: number;
  descriptionChannel: string | null;
  paymentRef: string | null;
}

interface OrderBulkResponseData {
  content: OrderBulkTransaction[];
  pageable: {
    totalPages: number;
    last: boolean;
    numberOfElements: number;
    totalElements: number;
    pageSize: number;
    pageNumber: number;
  };
}

interface ScbBatchGroup {
  batch_ref: string;
  filter_type: 'scb' | 'other' | 'all';
  from_account: string;
  exported_at: string;
  exported_by_name: string;
  total_amount: number;
  item_count: number;
  batch_status: string;
  payouts: {
    payout_id: string;
    payout_number: string;
    merchant_name: string;
    bank_name: string;
    bank_account_name: string;
    bank_account_number: string;
    net_amount: number;
    status: string;
  }[];
}

interface KtbBatchGroup {
  batch_ref: string;
  from_account: string;
  filename: string;
  exported_at: string;
  exported_by_name: string;
  total_amount: number;
  item_count: number;
  batch_status: string;
  payouts: ScbBatchGroup['payouts'];
}

// ===== KTB อัตโนมัติ (บอท) — shapes จาก /admin/payouts/auto-bulk/* และ /admin/payouts/bulk-batches =====
interface AutoBulkSettings {
  enabled: boolean;
  bankId: string | null;
  fromAccount: string | null;
  intervalMin: number;
  intervalMax: number;
  maxItems: number;
  maxTotal: number;
}

interface AutoBulkLimits {
  intervalMin: { min: number; max: number };
  intervalMax: { min: number; max: number };
  maxItems: { min: number; max: number };
  maxTotal: { min: number; max: number };
}

interface AutoBulkState {
  enabled?: boolean;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_result?: any;
  paused_reason?: string | null;
  interval_min?: number;
  interval_max?: number;
  updated_at?: string;
}

interface AutoBulkBankInfo {
  id: string;
  name: string;
  bank_code: string;
  account_number: string;
}

// ฟอร์มเก็บเป็น string ทั้งหมด (input number ว่างได้) — แปลงเป็นตัวเลขตอน save
interface AutoBulkForm {
  bankId: string;
  fromAccount: string;
  intervalMin: string;
  intervalMax: string;
  maxItems: string;
  maxTotal: string;
}

interface AutoBulkBlockingBatch {
  id: string;
  batch_ref: string;
  status: string;
  source: string;
  payout_count: number;
  total_amount: number;
  created_at: string;
  submitted_at: string | null;
}

interface AutoBulkStatus {
  enabled: boolean;
  state: AutoBulkState;
  blocking_batches: AutoBulkBlockingBatch[];
  server_time: string;
}

interface KtbAccountOption {
  accountRefId: string;
  accountNo: string;
  accountName: string;
  availableBalance: number;
}

type BulkBatchStatus = 'preparing' | 'submitting' | 'completed' | 'partial' | 'failed' | 'unknown' | 'cancelled';
type BulkBatchItemResult = 'pending' | 'submitted' | 'completed' | 'skipped' | 'failed' | 'unknown' | 'released';

interface BulkBatchItem {
  id: string;
  payout_id: string;
  amount: number;
  recipient: { bank_account: string; bank_code: string; amount: number } | null;
  result: BulkBatchItemResult;
  ktb_item: any;
  error: string | null;
  released_at: string | null;
  released_by_name: string | null;
  payout: {
    id: string;
    payout_number: string;
    merchant_id: string;
    bank_name: string;
    bank_account_number: string;
    bank_account_name: string;
    amount: number;
    status: string;
    merchant?: { name: string } | null;
  } | null;
}

interface BulkBatch {
  id: string;
  batch_ref: string;
  source: 'auto' | 'manual';
  status: BulkBatchStatus;
  bank_id: string;
  from_account: string;
  payout_count: number;
  total_amount: number;
  bulk_order_id: string | null;
  package_ref_no: string | null;
  bulk_status: string | null;
  error_message: string | null;
  triggered_by_name: string | null;
  submitted_at: string | null;
  finished_at: string | null;
  reconciled_at: string | null;
  reconciled_by_name: string | null;
  reconcile_note: string | null;
  created_at: string;
  items: BulkBatchItem[];
}

// batch ที่เปิด reconcile modal ได้จากทั้งตารางประวัติ (เต็ม) และ blocking list ใน status (ย่อ)
type BulkBatchLite = Pick<BulkBatch, 'id' | 'batch_ref' | 'status' | 'source' | 'payout_count' | 'total_amount'> & Partial<BulkBatch>;

interface ReconcileCheckResult {
  batch: BulkBatch;
  server: { found?: boolean; state?: string; phase?: string; bulk_order_id?: string; result?: any; error?: string } | null;
  ktb: { bulk_status?: string; package_ref_no?: string; from_account?: string; items?: BulkStatusItem[]; error?: string } | null;
  auto: { resolved: boolean; action?: string; ktb_status?: string | null; ktb_action?: string; reason?: string } | null;
}

interface AutoConfirmDef {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void> | void;
}

// batch ที่ยังไม่ปิด — ปล่อย item / บอทเริ่มรอบใหม่ไม่ได้
const BATCH_OPEN_STATUSES: string[] = ['preparing', 'submitting', 'unknown'];
const BATCH_RECONCILABLE_STATUSES: string[] = ['unknown', 'submitting', 'failed', 'partial'];

const BATCH_STATUS_MAP: Record<string, StatusDef> = {
  preparing:  { bg: 'bg-blue-100 dark:bg-blue-500/15',   text: 'text-blue-700 dark:text-blue-400',   icon: faClock,               label: 'กำลังเตรียม' },
  submitting: { bg: 'bg-blue-100 dark:bg-blue-500/15',   text: 'text-blue-700 dark:text-blue-400',   icon: faSpinner,             label: 'กำลังส่ง KTB' },
  completed:  { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle,         label: 'สำเร็จ' },
  partial:    { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faExclamationTriangle, label: 'สำเร็จบางส่วน' },
  failed:     { bg: 'bg-red-100 dark:bg-red-500/15',     text: 'text-red-700 dark:text-red-400',     icon: faTimesCircle,         label: 'ล้มเหลว' },
  unknown:    { bg: 'bg-red-100 dark:bg-red-500/15',     text: 'text-red-700 dark:text-red-400',     icon: faQuestionCircle,      label: 'ไม่ทราบผล — ต้องตรวจ' },
  cancelled:  { bg: 'bg-gray-100 dark:bg-slate-700',     text: 'text-gray-600 dark:text-slate-300',  icon: faBan,                 label: 'ยกเลิก' },
};

const BATCH_ITEM_RESULT_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-gray-100 dark:bg-slate-700',     text: 'text-gray-600 dark:text-slate-300',  icon: faClock,               label: 'รอส่ง' },
  submitted: { bg: 'bg-blue-100 dark:bg-blue-500/15',   text: 'text-blue-700 dark:text-blue-400',   icon: faSpinner,             label: 'ส่งแล้ว' },
  completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle,         label: 'สำเร็จ' },
  skipped:   { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faExclamationTriangle, label: 'ถูกข้าม' },
  failed:    { bg: 'bg-red-100 dark:bg-red-500/15',     text: 'text-red-700 dark:text-red-400',     icon: faTimesCircle,         label: 'ล้มเหลว' },
  unknown:   { bg: 'bg-red-100 dark:bg-red-500/15',     text: 'text-red-700 dark:text-red-400',     icon: faQuestionCircle,      label: 'ไม่ทราบผล' },
  released:  { bg: 'bg-gray-100 dark:bg-slate-700',     text: 'text-gray-600 dark:text-slate-300',  icon: faUnlock,              label: 'ปล่อยแล้ว' },
};

const AUTO_BATCH_PAGE_SIZE = 20;
const AUTO_STATUS_POLL_MS = 15_000;

const formatBaht = (n: number | string | null | undefined) =>
  `฿${Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDateTime = (v?: string | null) => (v ? dayjs(v).tz(TZ).format('DD/MM/YY HH:mm:ss') : '-');

// สรุป state.last_result ของ job เป็นข้อความสั้น — shape จาก jobs/autoBulkPayout.js (3 แบบ: error / skipped / batch)
function summarizeLastResult(r: any): string {
  if (!r) return '—';
  if (r.error) return `ผิดพลาด: ${r.error}${r.batch_ref ? ` (batch ${r.batch_ref})` : ''}`;
  if (r.skipped) {
    const reason = r.reason === 'no_candidates' ? 'ไม่มีรายการให้โอน' : (r.reason || 'ข้ามรอบ');
    const extra = typeof r.candidates === 'number'
      ? ` (รอโอน ${r.candidates}${typeof r.blocked === 'number' ? `, ติด batch ค้าง ${r.blocked}` : ''})`
      : '';
    return `ข้ามรอบ: ${reason}${extra}`;
  }
  if (r.batch_ref) {
    const status = BATCH_STATUS_MAP[r.status]?.label || r.status || '-';
    return `${r.batch_ref} · ${status} · สำเร็จ ${r.success_count ?? 0} / ล้มเหลว ${r.failed_count ?? 0}${r.total_amount != null ? ` · ${formatBaht(r.total_amount)}` : ''}`;
  }
  try { return JSON.stringify(r); } catch { return String(r); }
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

//\u2B50 SCB Anywhere generators \u0E22\u0E49\u0E32\u0E22\u0E44\u0E1B utils/scbExport (\u0E43\u0E0A\u0E49\u0E23\u0E48\u0E27\u0E21\u0E01\u0E31\u0E1A\u0E2B\u0E19\u0E49\u0E32 P2pPayoutsPage \u2014 export \u0E2A\u0E48\u0E27\u0E19\u0E04\u0E49\u0E32\u0E07 P2P)

function generateBatchRef(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear()).slice(2)}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

const MAX_SELECT = 100;
const MAX_AMOUNT = 1_000_000;

// ⭐ ช่วงยอดสำหรับกรอง (ติ๊กได้หลายช่วง — ไม่ติ๊กเลย = แสดงทั้งหมด)
// key คือ token ที่ส่งให้ backend (`amount_ranges`) — เพิ่มช่วงใหม่ได้โดยเพิ่มบรรทัดเดียว
const AMOUNT_RANGES = [
  { key: '1-10000', label: '1 – 10,000' },
  { key: '10000-50000', label: '10,000 – 50,000' },
  { key: '50000+', label: '50,000+' },
] as const;

const BulkTransferPage: React.FC = () => {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [amountRanges, setAmountRanges] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedPayouts, setSelectedPayouts] = useState<Payout[]>([]);

  // Merchant filter
  const [merchants, setMerchants] = useState<{ id: string; name: string; email: string }[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>('');

  // Bank/account selection
  const [withdrawBanks, setWithdrawBanks] = useState<any[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedFromAccount, setSelectedFromAccount] = useState<string>('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [needsBankSelection, setNeedsBankSelection] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState<any>(null);

  // Unknown bulk orders (pending + has transfer_response)
  const [unknownGroups, setUnknownGroups] = useState<any[]>([]);
  const [unknownLoading, setUnknownLoading] = useState(false);
  const [completingBulkId, setCompletingBulkId] = useState<string | null>(null);
  const [expandedUnknownId, setExpandedUnknownId] = useState<string | null>(null);
  const [confirmGroup, setConfirmGroup] = useState<any | null>(null);

  // Bulk order history
  const [bulkOrders, setBulkOrders] = useState<BulkOrder[]>([]);
  const [bulkOrdersLoading, setBulkOrdersLoading] = useState(false);
  const [bulkOrdersPage, setBulkOrdersPage] = useState(1);
  const [bulkOrdersTotal, setBulkOrdersTotal] = useState(0);
  const [bulkOrdersPageSize] = useState(10);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [checkingStatusId, setCheckingStatusId] = useState<string | null>(null);
  const [bulkStatusResult, setBulkStatusResult] = useState<BulkStatusResult | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [historySearchInput, setHistorySearchInput] = useState('');
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [historyEndDate, setHistoryEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedHistoryStartDate, setAppliedHistoryStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedHistoryEndDate, setAppliedHistoryEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  // Order Bulk / Bank Statement
  const [showOrderBulkModal, setShowOrderBulkModal] = useState(false);
  const [orderBulkLoading, setOrderBulkLoading] = useState(false);
  const [orderBulkData, setOrderBulkData] = useState<OrderBulkResponseData | null>(null);
  const [orderBulkDate, setOrderBulkDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [orderBulkPage, setOrderBulkPage] = useState(0);
  const [orderBulkAccounts, setOrderBulkAccounts] = useState<any[]>([]);
  const [orderBulkBanks, setOrderBulkBanks] = useState<any[]>([]);
  const [orderBulkSelectedAccountRefId, setOrderBulkSelectedAccountRefId] = useState('');
  const [orderBulkNeedsBankSelection, setOrderBulkNeedsBankSelection] = useState(false);
  const [orderBulkLoadingAccounts, setOrderBulkLoadingAccounts] = useState(false);
  const [orderBulkSelectedBankId, setOrderBulkSelectedBankId] = useState('');
  const [bankNameMap, setBankNameMap] = useState<Record<string, string>>({});
  const [busyAccounts, setBusyAccounts] = useState<Set<string>>(new Set());

  // Summary totals
  const [summaryTotal, setSummaryTotal] = useState<{ total_count: number; total_amount: number } | null>(null);

  // Transfer mode selection
  const [transferMode, setTransferMode] = useState<'ktb' | 'scb' | 'ktb_export' | 'ktb_auto' | null>(null);
  const [showModeModal, setShowModeModal] = useState(true);

  // SCB Anywhere export
  const [scbFilter, setScbFilter] = useState<'scb' | 'other' | 'all'>('all');
  const [excludeBBL, setExcludeBBL] = useState(false);
  const [showScbExportModal, setShowScbExportModal] = useState(false);
  const [scbExporting, setScbExporting] = useState(false);
  const [scbFromAccount, setScbFromAccount] = useState('');
  const [scbBanksId, setScbBanksId] = useState('');
  const [scbModalBanks, setScbModalBanks] = useState<any[]>([]);
  const [scbModalBanksLoading, setScbModalBanksLoading] = useState(false);
  const [scbBatchGroups, setScbBatchGroups] = useState<ScbBatchGroup[]>([]);
  const [scbGroupsLoading, setScbGroupsLoading] = useState(false);
  const [showScbCompleteConfirm, setShowScbCompleteConfirm] = useState<string | null>(null);
  const [completingScbBatch, setCompletingScbBatch] = useState(false);
  const [expandedScbGroup, setExpandedScbGroup] = useState<string | null>(null);
  const [scbReExportingRefs, setScbReExportingRefs] = useState<Set<string>>(new Set());
  const [busyPayoutIds, setBusyPayoutIds] = useState<Set<string>>(new Set());

  // SCB batch groups filters + pagination
  const [scbSearchInput, setScbSearchInput] = useState('');
  const [scbSearchTerm, setScbSearchTerm] = useState('');
  const [scbStartDate, setScbStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [scbEndDate, setScbEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedScbStartDate, setAppliedScbStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedScbEndDate, setAppliedScbEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [scbPage, setScbPage] = useState(1);
  const [scbTotal, setScbTotal] = useState(0);
  const scbPageSize = 10;

  // KTB Bulk export (file) — มิเรอร์ flow SCB
  const [showKtbExportModal, setShowKtbExportModal] = useState(false);
  const [ktbExporting, setKtbExporting] = useState(false);
  const [ktbFromAccount, setKtbFromAccount] = useState('');
  const [ktbBanksId, setKtbBanksId] = useState('');
  const [ktbModalBanks, setKtbModalBanks] = useState<any[]>([]);
  const [ktbModalBanksLoading, setKtbModalBanksLoading] = useState(false);
  const [ktbBatchGroups, setKtbBatchGroups] = useState<KtbBatchGroup[]>([]);
  const [ktbGroupsLoading, setKtbGroupsLoading] = useState(false);
  const [showKtbCompleteConfirm, setShowKtbCompleteConfirm] = useState<string | null>(null);
  const [completingKtbBatch, setCompletingKtbBatch] = useState(false);
  const [expandedKtbGroup, setExpandedKtbGroup] = useState<string | null>(null);
  const [ktbReExportingRefs, setKtbReExportingRefs] = useState<Set<string>>(new Set());

  // KTB batch groups filters + pagination
  const [ktbSearchInput, setKtbSearchInput] = useState('');
  const [ktbSearchTerm, setKtbSearchTerm] = useState('');
  const [ktbStartDate, setKtbStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [ktbEndDate, setKtbEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedKtbStartDate, setAppliedKtbStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedKtbEndDate, setAppliedKtbEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [ktbPage, setKtbPage] = useState(1);
  const ktbPageSize = 10;

  // ===== KTB อัตโนมัติ (บอท) =====
  const [autoSettings, setAutoSettings] = useState<AutoBulkSettings | null>(null);
  const [autoForm, setAutoForm] = useState<AutoBulkForm>({ bankId: '', fromAccount: '', intervalMin: '', intervalMax: '', maxItems: '', maxTotal: '' });
  const [autoLimits, setAutoLimits] = useState<AutoBulkLimits | null>(null);
  const [autoBank, setAutoBank] = useState<AutoBulkBankInfo | null>(null);
  const [autoState, setAutoState] = useState<AutoBulkState>({});
  const [autoSettingsLoading, setAutoSettingsLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoToggling, setAutoToggling] = useState(false);
  const [autoKtbBanks, setAutoKtbBanks] = useState<any[]>([]);
  const [autoAccounts, setAutoAccounts] = useState<KtbAccountOption[]>([]);
  const [autoAccountsLoading, setAutoAccountsLoading] = useState(false);
  const [showAutoAccountsModal, setShowAutoAccountsModal] = useState(false);
  const [autoAccountPick, setAutoAccountPick] = useState('');
  const [autoStatus, setAutoStatus] = useState<AutoBulkStatus | null>(null);
  const [autoStatusLoading, setAutoStatusLoading] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  // tick ทุก 1 วิ ให้ countdown เดิน (ค่าไม่ได้ใช้ตรง ๆ — แค่ trigger re-render)
  const [, setAutoTick] = useState(0);
  // server_time − นาฬิกาเครื่อง (ms) — กัน countdown เพี้ยนถ้าเครื่องแอดมินเวลาคลาด
  const autoClockOffsetRef = useRef(0);
  // true ระหว่าง PUT toggle — กัน poll สถานะ (ทุก 15 วิ) เอาค่า enabled เก่ามาทับ optimistic state
  const autoTogglingRef = useRef(false);
  const autoHistoryRef = useRef<HTMLDivElement>(null);

  // ประวัติ bulk batch (บอท + กดเอง)
  const [autoBatches, setAutoBatches] = useState<BulkBatch[]>([]);
  const [autoBatchesTotal, setAutoBatchesTotal] = useState(0);
  const [autoBatchesPage, setAutoBatchesPage] = useState(1);
  const [autoBatchesLoading, setAutoBatchesLoading] = useState(false);
  const [autoBatchStatusFilter, setAutoBatchStatusFilter] = useState('');
  const [autoBatchSourceFilter, setAutoBatchSourceFilter] = useState('');
  const [autoBatchStartDate, setAutoBatchStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [autoBatchEndDate, setAutoBatchEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedAutoBatchStartDate, setAppliedAutoBatchStartDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedAutoBatchEndDate, setAppliedAutoBatchEndDate] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [autoBatchSearchInput, setAutoBatchSearchInput] = useState('');
  const [autoBatchSearchTerm, setAutoBatchSearchTerm] = useState('');
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [releaseSelectedIds, setReleaseSelectedIds] = useState<Set<string>>(new Set());
  const [releasing, setReleasing] = useState(false);

  // Reconcile modal (ตรวจกับธนาคาร)
  const [reconcileBatch, setReconcileBatch] = useState<BulkBatchLite | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<ReconcileCheckResult | null>(null);
  const [reconcileNote, setReconcileNote] = useState('');
  const [reconcileSubmitting, setReconcileSubmitting] = useState(false);

  // Confirm dialog ร่วมของโหมดบอท (release / reconcile complete / reconcile fail)
  const [autoConfirm, setAutoConfirm] = useState<AutoConfirmDef | null>(null);
  const [autoConfirmBusy, setAutoConfirmBusy] = useState(false);

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isAdminEmployee = adminUser.type === 'admin_employee';
  const canApprove = isAdminEmployee ? adminUser.permissions?.approve_payouts === true : true;
  const isAutoMode = transferMode === 'ktb_auto';

  const totalSelected = selectedPayouts.reduce((sum, p) => sum + p.amount, 0);
  const isOverLimit = selectedIds.size > MAX_SELECT || totalSelected > MAX_AMOUNT;

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 800);
    return () => clearTimeout(t);
  }, [searchInput]);

  // History search debounce
  useEffect(() => {
    const t = setTimeout(() => { setHistorySearchTerm(historySearchInput); setBulkOrdersPage(1); }, 800);
    return () => clearTimeout(t);
  }, [historySearchInput]);

  const loadData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedMerchantId) params.append('merchant_id', selectedMerchantId);
      if (amountRanges.size > 0) {
        // ส่งตามลำดับใน AMOUNT_RANGES เพื่อให้ query string คงที่
        params.append('amount_ranges', AMOUNT_RANGES.filter(r => amountRanges.has(r.key)).map(r => r.key).join(','));
      }
      params.append('sort_order', sortOrder);
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));

      const [res, summaryRes] = await Promise.all([
        merchantApi.get(`/admin/payouts?${params.toString()}`),
        merchantApi.get(`/admin/payouts/summary?${params.toString()}`),
      ]);
      if (res.data.success) {
        setPayouts(res.data.data.payouts || []);
        setTotalItems(res.data.data.total || 0);
      }
      if (summaryRes.data.success) {
        setSummaryTotal(summaryRes.data.data);
      }
    } catch {
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, appliedDateFrom, appliedDateTo, searchTerm, selectedMerchantId, amountRanges, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch bank list for name mapping in history table
  useEffect(() => {
    merchantApi.get('/admin/payouts/withdraw-banks').then(res => {
      if (res.data.success) {
        const map: Record<string, string> = {};
        (res.data.data.banks || []).forEach((b: any) => { map[b.id] = b.name; });
        setBankNameMap(map);
      }
    }).catch(() => {});
  }, []);

  // Fetch merchant list for filter
  useEffect(() => {
    merchantApi.get('/merchants').then(res => {
      setMerchants(res.data.data || []);
    }).catch(() => {});
  }, []);

  const toggleSelect = (payout: Payout) => {
    if (selectedIds.has(payout.id)) {
      const next = new Set(selectedIds);
      next.delete(payout.id);
      setSelectedIds(next);
      setSelectedPayouts(sp => sp.filter(p => p.id !== payout.id));
    } else {
      if (selectedIds.size >= MAX_SELECT) {
        toast.warning(`เลือกได้สูงสุด ${MAX_SELECT} รายการ`);
        return;
      }
      const next = new Set(selectedIds);
      next.add(payout.id);
      setSelectedIds(next);
      setSelectedPayouts(sp => sp.some(p => p.id === payout.id) ? sp : [...sp, payout]);
    }
  };

  const toggleSelectAll = () => {
    const pageIds = displayedPayouts.map(p => p.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    if (allSelected) {
      const next = new Set(selectedIds);
      pageIds.forEach(id => next.delete(id));
      setSelectedIds(next);
      setSelectedPayouts(sp => sp.filter(p => !pageIds.includes(p.id)));
    } else {
      const toAdd = displayedPayouts.filter(p => !selectedIds.has(p.id));
      const totalAfter = selectedIds.size + toAdd.length;
      if (totalAfter > MAX_SELECT) {
        toast.warning(`เลือกได้สูงสุด ${MAX_SELECT} รายการ (ปัจจุบัน ${selectedIds.size} รายการ)`);
        return;
      }
      const next = new Set(selectedIds);
      toAdd.forEach(p => next.add(p.id));
      setSelectedIds(next);
      setSelectedPayouts(sp => {
        const existingIds = new Set(sp.map(p => p.id));
        return [...sp, ...toAdd.filter(p => !existingIds.has(p.id))];
      });
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedPayouts([]);
  };

  const loadScbBatchGroups = useCallback(async () => {
    setScbGroupsLoading(true);
    try {
      const params = new URLSearchParams();
      if (scbSearchTerm) params.append('search', scbSearchTerm);
      if (appliedScbStartDate) params.append('start_date', appliedScbStartDate);
      if (appliedScbEndDate) params.append('end_date', appliedScbEndDate);
      const res = await merchantApi.get(`/admin/payouts/scb-batch-groups?${params.toString()}`);
      if (res.data.success) {
        setScbBatchGroups(res.data.data.groups || []);
        setScbTotal(res.data.data.total || 0);
      }
    } catch { /* silent */ } finally {
      setScbGroupsLoading(false);
    }
  }, [scbSearchTerm, appliedScbStartDate, appliedScbEndDate]);

  const handleScbExportConfirm = async () => {
    if (!scbBanksId) { toast.warning('กรุณาเลือกบัญชีต้นทาง SCB'); return; }
    setScbExporting(true);
    try {
      const batch_ref = generateBatchRef();
      const payout_ids = selectedPayouts.map(p => p.id);
      const res = await merchantApi.post('/admin/payouts/scb-export-approve', {
        payout_ids, batch_ref, filter_type: scbFilter,
        from_account: scbFromAccount.trim(),
        banks_id: scbBanksId || undefined
      });
      if (res.data.success) {
        const fileContent = scbFilter === 'all'
          ? generateScbFileAll(selectedPayouts, scbFromAccount.trim())
          : generateScbFile(selectedPayouts, scbFromAccount.trim(), scbFilter);
        downloadTextFile(fileContent, `SCB_ANYWHERE_${batch_ref}.txt`);
        toast.success(`Export สำเร็จ ${selectedPayouts.length} รายการ`);
        setShowScbExportModal(false);
        setScbFromAccount('');
        clearSelection();
        await Promise.all([loadData(true), loadScbBatchGroups()]);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาดในการ Export');
    } finally {
      setScbExporting(false);
    }
  };

  const handleScbBatchComplete = async () => {
    if (!showScbCompleteConfirm) return;
    setCompletingScbBatch(true);
    try {
      const res = await merchantApi.post('/admin/payouts/scb-batch-complete', { batch_ref: showScbCompleteConfirm });
      if (res.data.success) {
        toast.success(`ปรับสถานะสำเร็จ ${res.data.data.completed_count} รายการ`);
        setShowScbCompleteConfirm(null);
        await loadScbBatchGroups();
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setCompletingScbBatch(false);
    }
  };

  // ⭐ โหลด export SCB Anywhere ของ batch เดิมใหม่ — ไฟล์ได้ ref วันที่ใหม่ (ใช้เมื่อ ref เดิมอัปโหลดเข้า SCB Anywhere ไม่ผ่าน)
  const handleScbReExport = async (batchRef: string) => {
    setScbReExportingRefs(prev => new Set(prev).add(batchRef));
    try {
      const res = await merchantApi.post('/admin/payouts/scb-re-export', { batch_ref: batchRef });
      if (res.data.success) {
        const { filter_type, from_account, payouts } = res.data.data;
        const fileContent = filter_type === 'all'
          ? generateScbFileAll(payouts, from_account)
          : generateScbFile(payouts, from_account, filter_type);
        const stamp = dayjs().tz(TZ).format('YYMMDDHHmmss');
        downloadTextFile(fileContent, `SCB_ANYWHERE_${batchRef}_${stamp}.txt`);
        toast.success(res.data.message || 'โหลดไฟล์ใหม่สำเร็จ');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดไฟล์ใหม่');
    } finally {
      setScbReExportingRefs(prev => { const next = new Set(prev); next.delete(batchRef); return next; });
    }
  };

  // ===== KTB Bulk export (file) handlers — มิเรอร์ flow SCB =====
  const loadKtbBatchGroups = useCallback(async () => {
    setKtbGroupsLoading(true);
    try {
      const params = new URLSearchParams();
      if (ktbSearchTerm) params.append('search', ktbSearchTerm);
      if (appliedKtbStartDate) params.append('start_date', appliedKtbStartDate);
      if (appliedKtbEndDate) params.append('end_date', appliedKtbEndDate);
      const res = await merchantApi.get(`/admin/payouts/ktb-batch-groups?${params.toString()}`);
      if (res.data.success) {
        setKtbBatchGroups(res.data.data.groups || []);
      }
    } catch { /* silent */ } finally {
      setKtbGroupsLoading(false);
    }
  }, [ktbSearchTerm, appliedKtbStartDate, appliedKtbEndDate]);

  const handleKtbExportConfirm = async () => {
    if (!ktbBanksId) { toast.warning('กรุณาเลือกบัญชีต้นทาง KTB'); return; }
    setKtbExporting(true);
    try {
      const batch_ref = generateBatchRef();
      const payout_ids = selectedPayouts.map(p => p.id);
      const res = await merchantApi.post('/admin/payouts/ktb-export-approve', {
        payout_ids, batch_ref,
        from_account: ktbFromAccount.trim(),
        banks_id: ktbBanksId
      });
      if (res.data.success) {
        const { csv, filename } = res.data.data;
        downloadTextFile(csv, filename || `KTB_BulkTransfer_${batch_ref}.csv`);
        toast.success(`Export สำเร็จ ${res.data.data.approved_count} รายการ`);
        setShowKtbExportModal(false);
        setKtbFromAccount('');
        setKtbBanksId('');
        clearSelection();
        await Promise.all([loadData(true), loadKtbBatchGroups()]);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาดในการ Export');
    } finally {
      setKtbExporting(false);
    }
  };

  const handleKtbBatchComplete = async () => {
    if (!showKtbCompleteConfirm) return;
    setCompletingKtbBatch(true);
    try {
      const res = await merchantApi.post('/admin/payouts/ktb-batch-complete', { batch_ref: showKtbCompleteConfirm });
      if (res.data.success) {
        toast.success(`ปรับสถานะสำเร็จ ${res.data.data.completed_count} รายการ`);
        setShowKtbCompleteConfirm(null);
        await loadKtbBatchGroups();
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setCompletingKtbBatch(false);
    }
  };

  // ⭐ สร้างไฟล์ CSV ของ batch เดิมใหม่ — ได้ Customer Reference No (ref วันที่) ใหม่ (ใช้เมื่อ ref เดิมอัปโหลดเข้า KTB Business ไม่ผ่าน)
  const handleKtbReExport = async (batchRef: string) => {
    setKtbReExportingRefs(prev => new Set(prev).add(batchRef));
    try {
      const res = await merchantApi.post('/admin/payouts/ktb-re-export', { batch_ref: batchRef });
      if (res.data.success) {
        const { csv, filename } = res.data.data;
        downloadTextFile(csv, filename);
        toast.success(res.data.message || 'สร้างไฟล์ใหม่สำเร็จ');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาดในการสร้างไฟล์ใหม่');
    } finally {
      setKtbReExportingRefs(prev => { const next = new Set(prev); next.delete(batchRef); return next; });
    }
  };

  const handleKtbHistorySearch = () => {
    if (dayjs(ktbStartDate).isAfter(dayjs(ktbEndDate), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedKtbStartDate(ktbStartDate);
    setAppliedKtbEndDate(ktbEndDate);
    setKtbPage(1);
  };

  const handleKtbHistoryReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setKtbStartDate(today);
    setKtbEndDate(today);
    setAppliedKtbStartDate(today);
    setAppliedKtbEndDate(today);
    setKtbSearchInput('');
    setKtbSearchTerm('');
    setKtbPage(1);
  };

  // ===== KTB อัตโนมัติ (บอท) — loaders / handlers =====
  // resetForm=false ตอน toggle: response คืน settings ที่บันทึกไว้ ถ้าเอามาทับฟอร์มจะทำค่าที่แอดมินกำลังพิมพ์หาย
  const applyAutoSettingsPayload = useCallback((data: any, resetForm: boolean) => {
    const s: AutoBulkSettings = data?.settings || {};
    setAutoSettings(s);
    if (resetForm) {
      setAutoForm({
        bankId: s.bankId || '',
        fromAccount: s.fromAccount || '',
        intervalMin: s.intervalMin != null ? String(s.intervalMin) : '',
        intervalMax: s.intervalMax != null ? String(s.intervalMax) : '',
        maxItems: s.maxItems != null ? String(s.maxItems) : '',
        maxTotal: s.maxTotal != null ? String(s.maxTotal) : '',
      });
    }
    setAutoBank(data?.bank || null);
    setAutoState(data?.state || {});
    if (data?.limits) setAutoLimits(data.limits);
  }, []);

  const loadAutoSettings = useCallback(async () => {
    setAutoSettingsLoading(true);
    try {
      const res = await merchantApi.get('/admin/payouts/auto-bulk/settings');
      if (res.data.success) applyAutoSettingsPayload(res.data.data, true);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถโหลดการตั้งค่าบอทได้');
    } finally {
      setAutoSettingsLoading(false);
    }
  }, [applyAutoSettingsPayload]);

  // silent=true (poll/socket) ไม่โชว์ spinner และไม่ toast error — กัน toast รัวทุก 15 วิถ้า API ล่ม
  const loadAutoStatus = useCallback(async (silent = true) => {
    if (!silent) setAutoStatusLoading(true);
    try {
      const res = await merchantApi.get('/admin/payouts/auto-bulk/status');
      if (res.data.success) {
        const data: AutoBulkStatus = res.data.data;
        setAutoStatus(data);
        setAutoState(data.state || {});
        if (!autoTogglingRef.current) setAutoSettings(prev => (prev ? { ...prev, enabled: !!data.enabled } : prev));
        if (data.server_time) autoClockOffsetRef.current = dayjs(data.server_time).valueOf() - Date.now();
      }
    } catch (e: any) {
      if (!silent) toast.error(e.response?.data?.message || 'ไม่สามารถโหลดสถานะบอทได้');
    } finally {
      if (!silent) setAutoStatusLoading(false);
    }
  }, []);

  const loadAutoBatches = useCallback(async () => {
    setAutoBatchesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(autoBatchesPage));
      params.append('page_size', String(AUTO_BATCH_PAGE_SIZE));
      if (autoBatchStatusFilter) params.append('status', autoBatchStatusFilter);
      if (autoBatchSourceFilter) params.append('source', autoBatchSourceFilter);
      if (appliedAutoBatchStartDate) params.append('start_date', appliedAutoBatchStartDate);
      if (appliedAutoBatchEndDate) params.append('end_date', appliedAutoBatchEndDate);
      if (autoBatchSearchTerm) params.append('search', autoBatchSearchTerm);
      const res = await merchantApi.get(`/admin/payouts/bulk-batches?${params.toString()}`);
      if (res.data.success) {
        setAutoBatches(res.data.data.batches || []);
        setAutoBatchesTotal(res.data.data.total || 0);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถโหลดประวัติ batch ได้');
    } finally {
      setAutoBatchesLoading(false);
    }
  }, [autoBatchesPage, autoBatchStatusFilter, autoBatchSourceFilter, appliedAutoBatchStartDate, appliedAutoBatchEndDate, autoBatchSearchTerm]);

  const refreshAutoAll = useCallback(() => {
    loadAutoStatus(true);
    loadAutoBatches();
  }, [loadAutoStatus, loadAutoBatches]);

  // สวิตช์หลัก — บันทึกทันที (optimistic) ถ้า backend ตอบ 400 (เช่น ยังไม่ผูกบัญชี) ให้ถอยกลับ + โชว์ข้อความ
  const handleAutoToggle = async (next: boolean) => {
    if (!autoSettings || autoToggling) return;
    const prev = autoSettings.enabled;
    setAutoSettings({ ...autoSettings, enabled: next });
    setAutoToggling(true);
    autoTogglingRef.current = true;
    try {
      const res = await merchantApi.put('/admin/payouts/auto-bulk/settings', { enabled: next });
      if (res.data.success) {
        applyAutoSettingsPayload(res.data.data, false);
        toast.success(next ? 'เปิดบอทแล้ว — จะเริ่มรอบแรกตามช่วงเวลาที่ตั้งไว้' : 'ปิดบอทแล้ว');
        autoTogglingRef.current = false;
        loadAutoStatus(true);
      }
    } catch (e: any) {
      setAutoSettings(s => (s ? { ...s, enabled: prev } : s));
      toast.error(e.response?.data?.message || 'ไม่สามารถเปลี่ยนสถานะบอทได้');
    } finally {
      autoTogglingRef.current = false;
      setAutoToggling(false);
    }
  };

  const handleAutoSave = async () => {
    const toNum = (v: string) => (v.trim() === '' ? NaN : Number(v));
    const intervalMin = toNum(autoForm.intervalMin);
    const intervalMax = toNum(autoForm.intervalMax);
    const maxItems = toNum(autoForm.maxItems);
    const maxTotal = toNum(autoForm.maxTotal);
    if ([intervalMin, intervalMax, maxItems, maxTotal].some(n => !Number.isFinite(n))) {
      toast.warning('กรุณากรอกตัวเลขให้ครบทุกช่อง');
      return;
    }
    if (intervalMin > intervalMax) {
      toast.warning('ช่วงเวลาต่ำสุดต้องไม่มากกว่าช่วงเวลาสูงสุด');
      return;
    }
    if (autoLimits) {
      const checks: [string, number, { min: number; max: number }][] = [
        ['รอบถี่สุด', intervalMin, autoLimits.intervalMin],
        ['รอบห่างสุด', intervalMax, autoLimits.intervalMax],
        ['จำนวนสูงสุดต่อรอบ', maxItems, autoLimits.maxItems],
        ['ยอดรวมสูงสุดต่อรอบ', maxTotal, autoLimits.maxTotal],
      ];
      const bad = checks.find(([, v, lim]) => v < lim.min || v > lim.max);
      if (bad) {
        toast.warning(`${bad[0]} ต้องอยู่ระหว่าง ${bad[2].min.toLocaleString()} – ${bad[2].max.toLocaleString()}`);
        return;
      }
    }
    setAutoSaving(true);
    try {
      const res = await merchantApi.put('/admin/payouts/auto-bulk/settings', {
        enabled: autoSettings?.enabled ?? false,
        bank_id: autoForm.bankId || null,
        from_account: autoForm.fromAccount.trim() || null,
        interval_min: intervalMin,
        interval_max: intervalMax,
        max_items: maxItems,
        max_total: maxTotal,
      });
      if (res.data.success) {
        applyAutoSettingsPayload(res.data.data, true);
        toast.success('บันทึกการตั้งค่าบอทแล้ว');
        loadAutoStatus(true);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'บันทึกการตั้งค่าไม่สำเร็จ');
    } finally {
      setAutoSaving(false);
    }
  };

  // ⚠️ ยิง /bank-accounts = ล็อกอิน KTB จริง (อาจมี OTP) — เรียกเฉพาะตอนกดปุ่มเท่านั้น ห้าม auto-load
  const handleLoadKtbAccounts = async () => {
    if (!autoForm.bankId) {
      toast.warning('กรุณาเลือกธนาคารก่อน');
      return;
    }
    setAutoAccountsLoading(true);
    try {
      const res = await merchantApi.post('/admin/payouts/bank-accounts', { bank_id: autoForm.bankId });
      if (res.data.success) {
        const accounts: KtbAccountOption[] = res.data.data.accounts || [];
        if (accounts.length === 0) {
          toast.warning('ไม่พบบัญชีใน KTB Business');
          return;
        }
        setAutoAccounts(accounts);
        setAutoAccountPick(accounts.find(a => a.accountNo === autoForm.fromAccount)?.accountNo || accounts[0].accountNo);
        setShowAutoAccountsModal(true);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถดึงบัญชีจาก KTB ได้');
    } finally {
      setAutoAccountsLoading(false);
    }
  };

  const handleRunNow = async () => {
    setAutoRunning(true);
    try {
      const res = await merchantApi.post('/admin/payouts/auto-bulk/run-now');
      toast.success(res.data.message || 'สั่งรันแล้ว — บอทจะเริ่มภายใน 15 วินาที');
      setTimeout(() => loadAutoStatus(true), 2000);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'สั่งรันไม่สำเร็จ');
    } finally {
      setAutoRunning(false);
    }
  };

  // useCallback เพราะถูกอ้างใน autoBatchColumns (useMemo) — ไม่งั้น columns สร้างใหม่ทุก render
  const openReconcileModal = useCallback(async (batch: BulkBatchLite) => {
    setReconcileBatch(batch);
    setReconcileResult(null);
    setReconcileNote('');
    setReconcileLoading(true);
    try {
      const res = await merchantApi.post(`/admin/payouts/bulk-batches/${batch.id}/reconcile`, { action: 'check' });
      if (res.data.success) {
        const data: ReconcileCheckResult = res.data.data;
        setReconcileResult(data);
        // batch จาก check = แถว DB ล่าสุด (ไม่มี items) — merge ทับเพื่อให้สถานะใน modal ตรงกับ DB
        if (data.batch) setReconcileBatch(prev => (prev ? { ...prev, ...data.batch } : data.batch));
        if (data.auto?.resolved) {
          toast.info(`ระบบสรุปผล batch นี้ให้อัตโนมัติแล้ว (${data.auto.action})`);
          refreshAutoAll();
        }
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ตรวจสอบกับธนาคารไม่สำเร็จ');
    } finally {
      setReconcileLoading(false);
    }
  }, [refreshAutoAll]);

  const submitReconcile = async (action: 'complete' | 'fail') => {
    if (!reconcileBatch) return;
    setReconcileSubmitting(true);
    try {
      const res = await merchantApi.post(`/admin/payouts/bulk-batches/${reconcileBatch.id}/reconcile`, {
        action,
        note: reconcileNote.trim() || undefined,
      });
      if (res.data.success) {
        toast.success(action === 'complete'
          ? `ปิด batch ${reconcileBatch.batch_ref} เป็น completed แล้ว`
          : `คืน payout ใน batch ${reconcileBatch.batch_ref} เป็น pending แล้ว`);
        setReconcileBatch(null);
        setReconcileResult(null);
        refreshAutoAll();
        loadData(true);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ดำเนินการไม่สำเร็จ');
    } finally {
      setReconcileSubmitting(false);
    }
  };

  const confirmReconcile = (action: 'complete' | 'fail') => {
    if (!reconcileBatch) return;
    const b = reconcileBatch;
    setAutoConfirm({
      title: action === 'complete' ? 'ยืนยันว่าโอนสำเร็จแล้ว' : 'ยืนยันว่าเงินไม่ออก',
      danger: action === 'fail',
      confirmLabel: action === 'complete' ? 'ปิดเป็น completed' : 'คืน pending',
      message: action === 'complete' ? (
        <>
          batch <strong>{b.batch_ref}</strong> ({b.payout_count} รายการ · {formatBaht(b.total_amount)}) จะถูกปิดเป็น <strong>completed</strong> —
          payout ทุกใบในชุดจะเป็น completed และส่ง webhook <strong>payout.completed</strong> ให้ร้านค้าทันที{' '}
          <span className="text-red-600 dark:text-red-400 font-semibold">ห้ามยืนยันถ้ายังไม่เห็นรายการโอนใน KTB Business</span>
        </>
      ) : (
        <>
          batch <strong>{b.batch_ref}</strong> ({b.payout_count} รายการ · {formatBaht(b.total_amount)}) จะถูกปิดเป็น <strong>failed</strong> และ
          payout ทุกใบคืนเป็น <strong>pending</strong> — บอท/แอดมินจะหยิบไปโอนใหม่ได้{' '}
          <span className="text-red-600 dark:text-red-400 font-semibold">ถ้าเงินออกไปแล้วจริง = ลูกค้าได้เงินซ้ำสองรอบ</span>
        </>
      ),
      onConfirm: () => submitReconcile(action),
    });
  };

  const toggleReleaseSelect = (id: string) => {
    setReleaseSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleReleaseItems = (batch: BulkBatch) => {
    const ids = batch.items
      .filter(i => releaseSelectedIds.has(i.id) && (i.result === 'failed' || i.result === 'skipped'))
      .map(i => i.id);
    if (ids.length === 0) {
      toast.warning('กรุณาเลือกรายการที่ต้องการปล่อย');
      return;
    }
    setAutoConfirm({
      title: 'ปล่อยให้บอทหยิบใหม่',
      confirmLabel: `ปล่อย ${ids.length} รายการ`,
      message: (
        <>
          ปล่อย <strong>{ids.length}</strong> รายการใน batch <strong>{batch.batch_ref}</strong> ให้บอทหยิบไปโอนใหม่ในรอบถัดไป —
          ตรวจให้แน่ใจว่ารายการเหล่านี้<strong>ยังไม่ได้เงินจริง</strong> ไม่งั้นจะโอนซ้ำ
        </>
      ),
      onConfirm: async () => {
        setReleasing(true);
        try {
          const res = await merchantApi.post(`/admin/payouts/bulk-batches/${batch.id}/release`, { item_ids: ids });
          toast.success(res.data.message || `ปล่อยแล้ว ${res.data.data?.released ?? ids.length} รายการ`);
          setReleaseSelectedIds(new Set());
          refreshAutoAll();
        } catch (e: any) {
          toast.error(e.response?.data?.message || 'ปล่อยรายการไม่สำเร็จ');
        } finally {
          setReleasing(false);
        }
      },
    });
  };

  const runAutoConfirm = async () => {
    if (!autoConfirm) return;
    setAutoConfirmBusy(true);
    try {
      await autoConfirm.onConfirm();
    } finally {
      setAutoConfirmBusy(false);
      setAutoConfirm(null);
    }
  };

  const handleAutoBatchSearch = () => {
    if (autoBatchStartDate && autoBatchEndDate && dayjs(autoBatchStartDate).isAfter(dayjs(autoBatchEndDate), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedAutoBatchStartDate(autoBatchStartDate);
    setAppliedAutoBatchEndDate(autoBatchEndDate);
    setAutoBatchesPage(1);
  };

  const handleAutoBatchReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setAutoBatchStartDate(today);
    setAutoBatchEndDate(today);
    setAppliedAutoBatchStartDate(today);
    setAppliedAutoBatchEndDate(today);
    setAutoBatchStatusFilter('');
    setAutoBatchSourceFilter('');
    setAutoBatchSearchInput('');
    setAutoBatchSearchTerm('');
    setAutoBatchesPage(1);
  };

  // จาก banner pause → กรอง status=unknown ทุกวัน (batch ค้างอาจเป็นของวันก่อน) แล้วเลื่อนไปที่ตาราง
  const jumpToUnknownBatches = () => {
    setAutoBatchStatusFilter('unknown');
    setAutoBatchSourceFilter('');
    setAutoBatchStartDate('');
    setAutoBatchEndDate('');
    setAppliedAutoBatchStartDate('');
    setAppliedAutoBatchEndDate('');
    setAutoBatchesPage(1);
    setTimeout(() => autoHistoryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // เข้าโหมดบอท: โหลดตั้งค่า + สถานะ + ธนาคาร KTB, poll สถานะทุก 15 วิ, tick countdown ทุก 1 วิ
  useEffect(() => {
    if (transferMode !== 'ktb_auto') return;
    loadAutoSettings();
    loadAutoStatus(false);
    merchantApi.get('/admin/payouts/withdraw-banks?require_credential=false&all_active=true&bank_code=KTB')
      .then(res => { if (res.data.success) setAutoKtbBanks(res.data.data.banks || []); })
      .catch(() => {});
    const poll = setInterval(() => loadAutoStatus(true), AUTO_STATUS_POLL_MS);
    const tick = setInterval(() => setAutoTick(v => v + 1), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [transferMode, loadAutoSettings, loadAutoStatus]);

  useEffect(() => {
    if (transferMode === 'ktb_auto') loadAutoBatches();
  }, [transferMode, loadAutoBatches]);

  // Debounce ค้นหาประวัติ batch
  useEffect(() => {
    const t = setTimeout(() => { setAutoBatchSearchTerm(autoBatchSearchInput); setAutoBatchesPage(1); }, 800);
    return () => clearTimeout(t);
  }, [autoBatchSearchInput]);

  // Socket ของโหมดบอท — ลงทะเบียนเฉพาะตอนอยู่ในโหมดนี้ (listener ของโหมดเดิมอยู่ใน effect ด้านล่างไม่แตะ)
  useEffect(() => {
    if (transferMode !== 'ktb_auto') return;
    const handleAutoBatch = (data: { batch_ref?: string; status?: string; source?: string; needs_reconcile?: boolean }) => {
      if (data?.status === 'unknown' || data?.needs_reconcile) {
        toast.warning(`batch ${data.batch_ref || ''} ผลการโอนไม่ชัดเจน — ต้องตรวจกับธนาคาร`, { autoClose: 8000 });
      }
      refreshAutoAll();
    };
    const handleGeneric = () => refreshAutoAll();
    socketService.on('auto-bulk-batch', handleAutoBatch);
    socketService.on('bulk-transfer-started', handleGeneric);
    socketService.on('bulk-transfer-ended', handleGeneric);
    return () => {
      socketService.off('auto-bulk-batch', handleAutoBatch);
      socketService.off('bulk-transfer-started', handleGeneric);
      socketService.off('bulk-transfer-ended', handleGeneric);
    };
  }, [transferMode, refreshAutoAll]);

  // Fetch banks for transfer
  const fetchWithdrawBanks = async () => {
    setLoadingAccounts(true);
    try {
      const res = await merchantApi.get('/admin/payouts/withdraw-banks');
      if (res.data.success) {
        const banks = res.data.data.banks || [];
        if (banks.length === 0) {
          toast.error('ไม่พบธนาคารที่ตั้งค่าสำหรับถอนเงินอัตโนมัติ');
        } else if (banks.length === 1) {
          setWithdrawBanks(banks);
          setNeedsBankSelection(false);
          await fetchBankAccounts(banks[0].id);
        } else {
          setWithdrawBanks(banks);
          setNeedsBankSelection(true);
        }
      }
    } catch (e: any) {
      console.log(e,'error transfer bulk');
      toast.error(e.response?.data?.message || e.message || 'เกิดข้อผิดพลาดในการโอนเงิน');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchBankAccounts = async (bankId: string) => {
    setLoadingAccounts(true);
    try {
      const res = await merchantApi.post('/admin/payouts/bank-accounts', { bank_id: bankId });
      if (res.data.success) {
        setBankAccounts(res.data.data.accounts || []);
        setSelectedBankId(res.data.data.bank_id);
        setNeedsBankSelection(false);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถดึงบัญชีได้');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const openTransferModal = async () => {
    if (selectedIds.size === 0) {
      toast.warning('กรุณาเลือกรายการที่ต้องการโอน');
      return;
    }
    setTransferResult(null);
    setBankAccounts([]);
    setSelectedFromAccount('');
    setWithdrawBanks([]);
    setSelectedBankId('');
    setNeedsBankSelection(false);
    setShowTransferModal(true);
    await fetchWithdrawBanks();
  };

  const handleTransfer = async () => {
    if (!selectedFromAccount) {
      toast.error('กรุณาเลือกบัญชีต้นทาง');
      return;
    }
    if (selectedIds.size === 0) {
      toast.error('กรุณาเลือกรายการที่ต้องการโอน');
      return;
    }

    try {
      setTransferring(true);
      const res = await merchantApi.post('/admin/payouts/bulk-transfer-auto', {
        payout_ids: Array.from(selectedIds),
        bank_id: selectedBankId,
        from_account: selectedFromAccount
      });

      if (res.data.success) {
        setTransferResult(res.data.data);
        toast.success(res.data.message);
        // Clear selection for successful items
        const failedIds = new Set<string>(
          (res.data.data.items || []).filter((i: any) => !i.success).map((i: any) => i.payout_id as string)
        );
        setSelectedIds(failedIds);
        setSelectedPayouts(sp => sp.filter(p => failedIds.has(p.id)));
        loadData(true);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'เกิดข้อผิดพลาดในการโอนเงิน');
    } finally {
      setTransferring(false);
    }
  };

  // Load bulk order history
  const loadBulkOrders = useCallback(async () => {
    try {
      setBulkOrdersLoading(true);
      const params = new URLSearchParams();
      params.append('limit', String(bulkOrdersPageSize));
      params.append('offset', String((bulkOrdersPage - 1) * bulkOrdersPageSize));
      if (historySearchTerm) params.append('search', historySearchTerm);
      if (appliedHistoryStartDate) params.append('start_date', appliedHistoryStartDate);
      if (appliedHistoryEndDate) params.append('end_date', appliedHistoryEndDate);

      const res = await merchantApi.get(`/admin/payouts/bulk-orders?${params.toString()}`);
      if (res.data.success) {
        setBulkOrders(res.data.data.bulk_orders || []);
        setBulkOrdersTotal(res.data.data.total || 0);
      }
    } catch {
      toast.error('ไม่สามารถโหลดประวัติ Bulk Order ได้');
    } finally {
      setBulkOrdersLoading(false);
    }
  }, [bulkOrdersPage, bulkOrdersPageSize, historySearchTerm, appliedHistoryStartDate, appliedHistoryEndDate]);

  useEffect(() => {
    loadBulkOrders();
  }, [loadBulkOrders]);

  const loadUnknownGroups = useCallback(async () => {
    try {
      setUnknownLoading(true);
      const res = await merchantApi.get('/admin/payouts/unknown-bulk-orders');
      if (res.data.success) {
        setUnknownGroups(res.data.data.groups || []);
      }
    } catch {
      toast.error('ไม่สามารถโหลดรายการที่ต้องตรวจสอบได้');
    } finally {
      setUnknownLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUnknownGroups();
  }, [loadUnknownGroups]);

  useEffect(() => {
    if (transferMode === 'scb') loadScbBatchGroups();
  }, [transferMode, loadScbBatchGroups]);

  useEffect(() => {
    if (transferMode === 'ktb_export') loadKtbBatchGroups();
  }, [transferMode, loadKtbBatchGroups]);

  // โหลด withdraw banks เมื่อ SCB export modal เปิด
  useEffect(() => {
    if (!showScbExportModal) return;
    setScbModalBanksLoading(true);
    merchantApi.get('/admin/payouts/withdraw-banks?require_credential=false&all_active=true&bank_code=SCB')
      .then(res => {
        if (res.data.success) setScbModalBanks(res.data.data.banks || []);
      })
      .catch(() => {})
      .finally(() => setScbModalBanksLoading(false));
    setScbBanksId('');
  }, [showScbExportModal]);

  // โหลด KTB withdraw banks เมื่อ KTB export modal เปิด
  useEffect(() => {
    if (!showKtbExportModal) return;
    setKtbModalBanksLoading(true);
    merchantApi.get('/admin/payouts/withdraw-banks?require_credential=false&all_active=true&bank_code=KTB')
      .then(res => {
        if (res.data.success) setKtbModalBanks(res.data.data.banks || []);
      })
      .catch(() => {})
      .finally(() => setKtbModalBanksLoading(false));
    setKtbBanksId('');
    setKtbFromAccount('');
  }, [showKtbExportModal]);

  // Debounce SCB search
  useEffect(() => {
    const t = setTimeout(() => { setScbSearchTerm(scbSearchInput); setScbPage(1); }, 800);
    return () => clearTimeout(t);
  }, [scbSearchInput]);

  // Debounce KTB search
  useEffect(() => {
    const t = setTimeout(() => { setKtbSearchTerm(ktbSearchInput); setKtbPage(1); }, 800);
    return () => clearTimeout(t);
  }, [ktbSearchInput]);

  const handleCompleteUnknownBulk = async () => {
    if (!confirmGroup) return;
    try {
      setCompletingBulkId(confirmGroup.bulk_order_id);
      const res = await merchantApi.post('/admin/payouts/bulk-complete-unknown', { bulk_order_id: confirmGroup.bulk_order_id });
      if (res.data.success) {
        toast.success(res.data.message);
        setConfirmGroup(null);
        loadUnknownGroups();
        loadBulkOrders();
        loadData(true);
      } else {
        toast.error(res.data.message || 'เกิดข้อผิดพลาด');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'เกิดข้อผิดพลาด');
    } finally {
      setCompletingBulkId(null);
    }
  };

  useEffect(() => {
    const handleBulkStarted = (data: { from_account: string; admin_name: string; payout_count: number }) => {
      setBusyAccounts(prev => new Set(prev).add(data.from_account));
      toast.info(`${data.admin_name} กำลังโอน ${data.payout_count} รายการจากบัญชี ${data.from_account}`, { autoClose: 5000 });
    };
    const handleBulkEnded = (data: { from_account: string }) => {
      setBusyAccounts(prev => {
        const next = new Set(prev);
        next.delete(data.from_account);
        return next;
      });
      loadData(true);
      loadBulkOrders();
    };
    const handleScbExportStarted = (data: { payout_ids: string[]; admin_name: string; payout_count: number }) => {
      setBusyPayoutIds(prev => {
        const next = new Set(prev);
        data.payout_ids.forEach(id => next.add(id));
        return next;
      });
      toast.info(`${data.admin_name} กำลัง Export SCB ${data.payout_count} รายการ`, { autoClose: 5000 });
    };
    const handleScbExportEnded = (data: { payout_ids: string[] }) => {
      setBusyPayoutIds(prev => {
        const next = new Set(prev);
        data.payout_ids.forEach(id => next.delete(id));
        return next;
      });
      loadData(true);
      loadScbBatchGroups();
    };
    const handleKtbExportStarted = (data: { payout_ids: string[]; admin_name: string; payout_count: number }) => {
      setBusyPayoutIds(prev => {
        const next = new Set(prev);
        data.payout_ids.forEach(id => next.add(id));
        return next;
      });
      toast.info(`${data.admin_name} กำลัง Export KTB ${data.payout_count} รายการ`, { autoClose: 5000 });
    };
    const handleKtbExportEnded = (data: { payout_ids: string[] }) => {
      setBusyPayoutIds(prev => {
        const next = new Set(prev);
        data.payout_ids.forEach(id => next.delete(id));
        return next;
      });
      loadData(true);
      loadKtbBatchGroups();
    };

    socketService.on('bulk-transfer-started', handleBulkStarted);
    socketService.on('bulk-transfer-ended', handleBulkEnded);
    socketService.on('scb-export-started', handleScbExportStarted);
    socketService.on('scb-export-ended', handleScbExportEnded);
    socketService.on('ktb-export-started', handleKtbExportStarted);
    socketService.on('ktb-export-ended', handleKtbExportEnded);
    return () => {
      socketService.off('bulk-transfer-started', handleBulkStarted);
      socketService.off('bulk-transfer-ended', handleBulkEnded);
      socketService.off('scb-export-started', handleScbExportStarted);
      socketService.off('scb-export-ended', handleScbExportEnded);
      socketService.off('ktb-export-started', handleKtbExportStarted);
      socketService.off('ktb-export-ended', handleKtbExportEnded);
    };
  }, [loadData, loadBulkOrders, loadScbBatchGroups, loadKtbBatchGroups]);

  const checkBulkOrderStatus = async (order: BulkOrder) => {
    try {
      setCheckingStatusId(order.bulk_order_id);
      const res = await merchantApi.post('/admin/payouts/bulk-order-status', {
        bulk_order_id: order.bulk_order_id,
        banks_id: order.banks_id,
        current_bulk_status: order.bulk_status
      });
      if (res.data.success) {
        setBulkStatusResult(res.data.data);
        setShowStatusModal(true);
      } else {
        toast.error(res.data.message || 'ไม่สามารถเช็คสถานะได้');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถเช็คสถานะจากธนาคารได้');
    } finally {
      setCheckingStatusId(null);
    }
  };

  const openOrderBulkModal = async () => {
    setOrderBulkData(null);
    setOrderBulkPage(0);
    setOrderBulkAccounts([]);
    setOrderBulkBanks([]);
    setOrderBulkSelectedAccountRefId('');
    setOrderBulkNeedsBankSelection(false);
    setOrderBulkSelectedBankId('');
    setShowOrderBulkModal(true);
    setOrderBulkLoadingAccounts(true);
    try {
      const res = await merchantApi.get('/admin/payouts/withdraw-banks');
      if (res.data.success) {
        const banks = res.data.data.banks || [];
        if (banks.length === 1) {
          setOrderBulkBanks(banks);
          const res2 = await merchantApi.post('/admin/payouts/bank-accounts', { bank_id: banks[0].id });
          if (res2.data.success) {
            setOrderBulkAccounts(res2.data.data.accounts || []);
            setOrderBulkSelectedBankId(res2.data.data.bank_id);
          }
        } else {
          setOrderBulkBanks(banks);
          setOrderBulkNeedsBankSelection(true);
        }
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดธนาคาร');
    } finally {
      setOrderBulkLoadingAccounts(false);
    }
  };

  const fetchOrderBulkAccounts = async (bankId: string) => {
    setOrderBulkLoadingAccounts(true);
    try {
      const res = await merchantApi.post('/admin/payouts/bank-accounts', { bank_id: bankId });
      if (res.data.success) {
        setOrderBulkAccounts(res.data.data.accounts || []);
        setOrderBulkSelectedBankId(res.data.data.bank_id);
        setOrderBulkNeedsBankSelection(false);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ไม่สามารถดึงบัญชีได้');
    } finally {
      setOrderBulkLoadingAccounts(false);
    }
  };

  const fetchOrderBulk = async (page = 0) => {
    if (!orderBulkSelectedAccountRefId) {
      toast.warning('กรุณาเลือกบัญชีก่อน');
      return;
    }
    setOrderBulkLoading(true);
    try {
      const res = await merchantApi.post('/admin/payouts/get-order-bulk', {
        bank_id: orderBulkSelectedBankId,
        account_ref_id: orderBulkSelectedAccountRefId,
        from_value_date: orderBulkDate,
        page_size: 25,
        page_number: page
      });
      if (res.data.success) {
        setOrderBulkData(res.data.data);
        setOrderBulkPage(page);
      } else {
        toast.error(res.data.message || 'ไม่สามารถดึงข้อมูลได้');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
    } finally {
      setOrderBulkLoading(false);
    }
  };

  const getBulkStatusBadge = (status: string) => {
    const s = status?.toUpperCase();
    if (s === 'SUCCESSFUL' || s === 'SUCCESS' || s === 'SUBMITTED' || s === 'SCHEDULED' || s === 'DRAFT') {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
        <FontAwesomeIcon icon={faCheckCircle} /> {status}
      </span>;
    }
    if (s === 'PROCESSING') {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-medium">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> {status}
      </span>;
    }
    if (s === 'FAILED' || s === 'REJECTED') {
      return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs font-medium">
        <FontAwesomeIcon icon={faTimesCircle} /> {status}
      </span>;
    }
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-full text-xs font-medium">{status}</span>;
  };

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setSearchInput('');
    setSearchTerm('');
    setSelectedMerchantId('');
    setAmountRanges(new Set());
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const toggleAmountRange = (key: string) => {
    setAmountRanges(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setCurrentPage(1);
  };

  const handleHistorySearch = () => {
    if (dayjs(historyStartDate).isAfter(dayjs(historyEndDate), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedHistoryStartDate(historyStartDate);
    setAppliedHistoryEndDate(historyEndDate);
    setBulkOrdersPage(1);
  };

  const handleHistoryReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setHistoryStartDate(today);
    setHistoryEndDate(today);
    setAppliedHistoryStartDate(today);
    setAppliedHistoryEndDate(today);
    setHistorySearchInput('');
    setHistorySearchTerm('');
    setBulkOrdersPage(1);
  };

  const handleScbHistorySearch = () => {
    if (dayjs(scbStartDate).isAfter(dayjs(scbEndDate), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedScbStartDate(scbStartDate);
    setAppliedScbEndDate(scbEndDate);
    setScbPage(1);
  };

  const handleScbHistoryReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setScbStartDate(today);
    setScbEndDate(today);
    setAppliedScbStartDate(today);
    setAppliedScbEndDate(today);
    setScbSearchInput('');
    setScbSearchTerm('');
    setScbPage(1);
  };

  const statCards: StatCardDef[] = [
    {
      icon: faLayerGroup,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รายการทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>{totalItems.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รายการที่ตรงกับตัวกรอง</span>,
    },
    {
      icon: faMoneyBillWave,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'ยอดรวมทั้งหมด',
      value: (
        <span className="text-orange-600 dark:text-orange-400 text-sm">
          {summaryTotal ? `฿${summaryTotal.total_amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}` : '...'}
        </span>
      ),
      subValue: (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {summaryTotal ? `${summaryTotal.total_count.toLocaleString()} รายการทั้งหมด` : 'กำลังโหลด...'}
        </span>
      ),
    },
    {
      icon: faCheck,
      iconClassName: selectedIds.size > MAX_SELECT
        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
        : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'รายการที่เลือก',
      value: (
        <span className={selectedIds.size > MAX_SELECT ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}>
          {selectedIds.size} / {MAX_SELECT}
        </span>
      ),
      subValue: (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {selectedIds.size > MAX_SELECT ? 'เกินจำนวนสูงสุด!' : selectedIds.size > 0 ? 'พร้อมโอน' : 'ยังไม่ได้เลือก'}
        </span>
      ),
    },
    {
      icon: faArrowRightArrowLeft,
      iconClassName: totalSelected > MAX_AMOUNT
        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ยอดที่จะโอน',
      value: (
        <span className={`text-sm tabular-nums ${totalSelected > MAX_AMOUNT ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
          ฿{totalSelected.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </span>
      ),
      subValue: (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {totalSelected > MAX_AMOUNT
            ? `เกินวงเงิน ฿${MAX_AMOUNT.toLocaleString()}!`
            : selectedIds.size > 0
              ? `วงเงินคงเหลือ ฿${(MAX_AMOUNT - totalSelected).toLocaleString()}`
              : 'วงเงินสูงสุด ฿1,000,000'}
        </span>
      ),
    },
  ];

  const totalPages = Math.ceil(totalItems / pageSize);

  // SCB pagination (client-side on grouped results)
  const pagedScbGroups = scbBatchGroups.slice((scbPage - 1) * scbPageSize, scbPage * scbPageSize);
  // KTB pagination (client-side on grouped results)
  const pagedKtbGroups = ktbBatchGroups.slice((ktbPage - 1) * ktbPageSize, ktbPage * ktbPageSize);

  const displayedPayouts = transferMode === 'scb'
    ? (scbFilter === 'scb'
        ? payouts.filter(p => getBankCode(p.bank_name) === '014')
        : scbFilter === 'other'
          ? payouts.filter(p => getBankCode(p.bank_name) !== '014' && (!excludeBBL || p.bank_name !== 'BBL'))
          : excludeBBL
            ? payouts.filter(p => p.bank_name !== 'BBL')
            : payouts)
    : payouts;

  const pageAllSelected = displayedPayouts.length > 0 && displayedPayouts.every(p => selectedIds.has(p.id));

  const expandedBulkOrder = bulkOrders.find(o => o.bulk_order_id === expandedOrderId) ?? null;
  const expandedScbBatch = scbBatchGroups.find(g => g.batch_ref === expandedScbGroup) ?? null;
  const expandedKtbBatch = ktbBatchGroups.find(g => g.batch_ref === expandedKtbGroup) ?? null;

  const bulkOrderItemColumns: Column<BulkOrder['items'][0]>[] = useMemo(() => [
    {
      key: 'index',
      header: '#',
      width: '48px',
      render: (_, idx) => <span className="text-gray-400 dark:text-slate-500 text-xs">{idx + 1}</span>,
    },
    {
      key: 'payout_number',
      header: 'เลขที่ Payout',
      render: (item) => <span className="font-medium text-gray-800 dark:text-slate-100">{item.payout_number}</span>,
    },
    {
      key: 'bank',
      header: 'ธนาคาร / เลขบัญชี',
      render: (item) => (
        <>
          <span className="text-gray-500 dark:text-slate-400">{item.bank_name} — </span>
          <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 py-0.5 rounded">{item.bank_account_number}</code>
        </>
      ),
    },
    {
      key: 'bank_account_name',
      header: 'ชื่อบัญชี',
      render: (item) => <span className="text-gray-700 dark:text-slate-300">{item.bank_account_name}</span>,
    },
    {
      key: 'amount',
      header: 'จำนวน',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (item) => <span className="font-semibold text-green-700 dark:text-green-400">฿{Number(item.amount).toLocaleString()}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (item) => (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs">
          <FontAwesomeIcon icon={faCheckCircle} /> {item.status}
        </span>
      ),
    },
  ], []);

  const bulkOrderColumns: Column<BulkOrder>[] = useMemo(() => [
    {
      key: 'bulk_order_id',
      header: 'Bulk Order ID',
      render: (order) => (
        <code className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded font-mono break-all">
          {order.bulk_order_id}
        </code>
      ),
    },
    {
      key: 'package_ref_no',
      header: 'Ref No.',
      render: (order) => <span className="text-xs text-gray-600 dark:text-slate-300">{order.package_ref_no}</span>,
    },
    {
      key: 'bulk_status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (order) => getBulkStatusBadge(order.bulk_status),
    },
    {
      key: 'item_count',
      header: 'จำนวนรายการ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (order) => (
        <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-bold">
          {order.item_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'ยอดรวม',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (order) => <span className="font-bold text-green-700 dark:text-green-400">฿{order.total_amount.toLocaleString()}</span>,
    },
    {
      key: 'from_account',
      header: 'บัญชีต้นทาง',
      render: (order) => order.from_account ? (
        <div>
          {bankNameMap[order.banks_id] && (
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-0.5">{bankNameMap[order.banks_id]}</p>
          )}
          <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded font-mono">
            {order.from_account}
          </code>
        </div>
      ) : <span className="text-xs text-gray-400 dark:text-slate-500">-</span>,
    },
    {
      key: 'approved_by_name',
      header: 'ผู้ดำเนินการ',
      render: (order) => <span className="text-xs text-gray-600 dark:text-slate-300">{order.approved_by_name || '-'}</span>,
    },
    {
      key: 'completed_at',
      header: 'วันที่',
      render: (order) => order.completed_at ? (
        <>
          <p className="text-xs text-gray-700 dark:text-slate-300">{new Date(order.completed_at).toLocaleDateString('th-TH')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            {new Date(order.completed_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </>
      ) : null,
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (order) => (
        <div className="flex items-center justify-center gap-1.5">
          <button
            type="button"
            onClick={() => setExpandedOrderId(expandedOrderId === order.bulk_order_id ? null : order.bulk_order_id)}
            className={`${TABLE_ACTION_BTN} border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-300`}
            title="ดูรายการ Payout"
          >
            <FontAwesomeIcon icon={expandedOrderId === order.bulk_order_id ? faChevronUp : faChevronDown} />
          </button>
          <button
            type="button"
            onClick={() => checkBulkOrderStatus(order)}
            disabled={checkingStatusId === order.bulk_order_id}
            className={`${TABLE_ACTION_BTN} bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800`}
            title="เช็คสถานะจากธนาคาร"
          >
            {checkingStatusId === order.bulk_order_id ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faEye} />
            )}
          </button>
        </div>
      ),
    },
  ], [bankNameMap, checkingStatusId, expandedOrderId]);

  const scbPayoutItemColumns: Column<ScbBatchGroup['payouts'][0]>[] = useMemo(() => [
    {
      key: 'payout_number',
      header: 'เลขที่ Payout',
      render: (p) => <span className="font-medium text-gray-800 dark:text-slate-100">{p.payout_number}</span>,
    },
    {
      key: 'merchant_name',
      header: 'Merchant',
      render: (p) => <span className="text-gray-500 dark:text-slate-400">{p.merchant_name}</span>,
    },
    {
      key: 'bank',
      header: 'ธนาคาร — เลขบัญชี',
      render: (p) => (
        <>
          <span className="text-gray-500 dark:text-slate-400">{p.bank_name} — </span>
          <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 py-0.5 rounded">{p.bank_account_number}</code>
        </>
      ),
    },
    {
      key: 'bank_account_name',
      header: 'ชื่อบัญชี',
      render: (p) => <span className="text-gray-700 dark:text-slate-300">{p.bank_account_name}</span>,
    },
    {
      key: 'net_amount',
      header: 'ยอดสุทธิ',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (p) => <span className="font-semibold text-green-700 dark:text-green-400">฿{Number(p.net_amount).toLocaleString()}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (p) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'}`}>
          {p.status}
        </span>
      ),
    },
  ], []);

  const scbBatchColumns: Column<ScbBatchGroup>[] = useMemo(() => [
    {
      key: 'expand',
      header: '',
      width: '40px',
      render: (group) => (
        <button
          type="button"
          onClick={() => setExpandedScbGroup(expandedScbGroup === group.batch_ref ? null : group.batch_ref)}
          className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
        >
          <FontAwesomeIcon icon={expandedScbGroup === group.batch_ref ? faChevronUp : faChevronDown} />
        </button>
      ),
    },
    {
      key: 'batch_ref',
      header: 'Batch Ref',
      render: (group) => (
        <code className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded font-mono">{group.batch_ref}</code>
      ),
    },
    {
      key: 'filter_type',
      header: 'ประเภท',
      render: (group) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${group.filter_type === 'scb' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : group.filter_type === 'all' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300'}`}>
          {group.filter_type === 'scb' ? 'โอนไปยัง SCB' : group.filter_type === 'all' ? 'ทั้งหมด' : 'ธนาคารอื่นๆ'}
        </span>
      ),
    },
    {
      key: 'from_account',
      header: 'บัญชีต้นทาง',
      render: (group) => (
        <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded font-mono">{group.from_account || '-'}</code>
      ),
    },
    {
      key: 'exported_at',
      header: 'Export เมื่อ',
      render: (group) => (
        <span className="text-xs text-gray-500 dark:text-slate-400">
          {group.exported_at ? dayjs(group.exported_at).tz(TZ).format('DD/MM/YY HH:mm') : '-'}
        </span>
      ),
    },
    {
      key: 'exported_by_name',
      header: 'โดย',
      render: (group) => <span className="text-xs text-gray-600 dark:text-slate-300">{group.exported_by_name || '-'}</span>,
    },
    {
      key: 'item_count',
      header: 'จำนวน',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-bold">
          {group.item_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'ยอดรวม',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (group) => (
        <span className="font-bold text-green-700 dark:text-green-400">
          ฿{Number(group.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'batch_status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        (group.batch_status === 'completed' || !group.payouts.some(p => p.status === 'approved')) ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
            <FontAwesomeIcon icon={faCheckCircle} /> completed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-medium">
            <FontAwesomeIcon icon={faExclamationTriangle} /> approved
          </span>
        )
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        group.batch_status !== 'completed' && group.payouts.some(p => p.status === 'approved') && canApprove ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => handleScbReExport(group.batch_ref)}
              disabled={scbReExportingRefs.has(group.batch_ref)}
              title="โหลดไฟล์ใหม่ — ใช้เมื่อ ref เดิม (ตามวันที่) อัปโหลดเข้า SCB Anywhere ไม่ผ่าน"
              className={`${TABLE_ACTION_BTN} bg-blue-600 hover:bg-blue-700 text-white border-transparent flex items-center justify-center gap-1.5 whitespace-nowrap`}
            >
              <FontAwesomeIcon icon={faRotate} className={scbReExportingRefs.has(group.batch_ref) ? 'animate-spin' : ''} />
              โหลด Export ใหม่
            </button>
            <button
              type="button"
              onClick={() => setShowScbCompleteConfirm(group.batch_ref)}
              className={`${TABLE_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white border-transparent flex items-center justify-center gap-1.5 whitespace-nowrap`}
            >
              <FontAwesomeIcon icon={faCheckCircle} />
              ปรับเป็น Completed
            </button>
          </div>
        ) : null
      ),
    },
  ], [canApprove, expandedScbGroup, scbReExportingRefs]);

  const ktbBatchColumns: Column<KtbBatchGroup>[] = useMemo(() => [
    {
      key: 'expand',
      header: '',
      width: '40px',
      render: (group) => (
        <button
          type="button"
          onClick={() => setExpandedKtbGroup(expandedKtbGroup === group.batch_ref ? null : group.batch_ref)}
          className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
        >
          <FontAwesomeIcon icon={expandedKtbGroup === group.batch_ref ? faChevronUp : faChevronDown} />
        </button>
      ),
    },
    {
      key: 'batch_ref',
      header: 'Batch Ref',
      render: (group) => (
        <code className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded font-mono">{group.batch_ref}</code>
      ),
    },
    {
      key: 'from_account',
      header: 'บัญชีต้นทาง',
      render: (group) => (
        <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded font-mono">{group.from_account || '-'}</code>
      ),
    },
    {
      key: 'exported_at',
      header: 'Export เมื่อ',
      render: (group) => (
        <span className="text-xs text-gray-500 dark:text-slate-400">
          {group.exported_at ? dayjs(group.exported_at).tz(TZ).format('DD/MM/YY HH:mm') : '-'}
        </span>
      ),
    },
    {
      key: 'exported_by_name',
      header: 'โดย',
      render: (group) => <span className="text-xs text-gray-600 dark:text-slate-300">{group.exported_by_name || '-'}</span>,
    },
    {
      key: 'item_count',
      header: 'จำนวน',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-bold">
          {group.item_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'ยอดรวม',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (group) => (
        <span className="font-bold text-green-700 dark:text-green-400">
          ฿{Number(group.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      key: 'batch_status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        (group.batch_status === 'completed' || !group.payouts.some(p => p.status === 'approved')) ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
            <FontAwesomeIcon icon={faCheckCircle} /> completed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-medium">
            <FontAwesomeIcon icon={faExclamationTriangle} /> approved
          </span>
        )
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (group) => (
        group.batch_status !== 'completed' && group.payouts.some(p => p.status === 'approved') && canApprove ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-1.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => handleKtbReExport(group.batch_ref)}
              disabled={ktbReExportingRefs.has(group.batch_ref)}
              title="สร้างไฟล์ใหม่ — ใช้เมื่อ ref เดิม (ตามวันที่) อัปโหลดเข้า KTB Business ไม่ผ่าน"
              className={`${TABLE_ACTION_BTN} bg-blue-600 hover:bg-blue-700 text-white border-transparent flex items-center justify-center gap-1.5 whitespace-nowrap`}
            >
              <FontAwesomeIcon icon={faRotate} className={ktbReExportingRefs.has(group.batch_ref) ? 'animate-spin' : ''} />
              โหลด Export ใหม่
            </button>
            <button
              type="button"
              onClick={() => setShowKtbCompleteConfirm(group.batch_ref)}
              className={`${TABLE_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white border-transparent flex items-center justify-center gap-1.5 whitespace-nowrap`}
            >
              <FontAwesomeIcon icon={faCheckCircle} />
              ปรับเป็น Completed
            </button>
          </div>
        ) : null
      ),
    },
  ], [canApprove, expandedKtbGroup, ktbReExportingRefs]);

  // ===== KTB อัตโนมัติ (บอท) — ตารางประวัติ batch =====
  const expandedBatch = useMemo(
    () => autoBatches.find(b => b.id === expandedBatchId) || null,
    [autoBatches, expandedBatchId],
  );
  const expandedBatchClosed = !!expandedBatch && !BATCH_OPEN_STATUSES.includes(expandedBatch.status);

  const autoBatchColumns: Column<BulkBatch>[] = useMemo(() => [
    {
      key: 'expand',
      header: '',
      width: '40px',
      render: (b) => (
        <button
          type="button"
          onClick={() => { setExpandedBatchId(expandedBatchId === b.id ? null : b.id); setReleaseSelectedIds(new Set()); }}
          className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
          title="ดูรายการใน batch"
        >
          <FontAwesomeIcon icon={expandedBatchId === b.id ? faChevronUp : faChevronDown} />
        </button>
      ),
    },
    {
      key: 'batch_ref',
      header: 'Batch Ref',
      render: (b) => (
        <code className="text-xs bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded font-mono">{b.batch_ref}</code>
      ),
    },
    {
      key: 'source',
      header: 'แหล่ง',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (b) => b.source === 'auto' ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
          <FontAwesomeIcon icon={faRobot} /> บอท
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
          กดเอง
        </span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (b) => <StatusBadge status={b.status} statusMap={BATCH_STATUS_MAP} pulse={b.status === 'unknown'} />,
    },
    {
      key: 'payout_count',
      header: 'จำนวน',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (b) => (
        <span className="inline-flex items-center justify-center w-7 h-7 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-xs font-bold">
          {b.payout_count}
        </span>
      ),
    },
    {
      key: 'total_amount',
      header: 'ยอดรวม',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (b) => <span className="font-bold text-green-700 dark:text-green-400">{formatBaht(b.total_amount)}</span>,
    },
    {
      key: 'bulk_order_id',
      header: 'Bulk Order / Ref No',
      render: (b) => (b.bulk_order_id || b.package_ref_no) ? (
        <div className="space-y-0.5">
          {b.bulk_order_id && <code className="block text-[11px] font-mono text-purple-700 dark:text-purple-300 break-all">{b.bulk_order_id}</code>}
          {b.package_ref_no && <code className="block text-[11px] font-mono text-gray-500 dark:text-slate-400">{b.package_ref_no}</code>}
          {b.bulk_status && <span className="block text-[10px] text-gray-400 dark:text-slate-500">{b.bulk_status}</span>}
        </div>
      ) : <span className="text-xs text-gray-400 dark:text-slate-500">-</span>,
    },
    {
      key: 'from_account',
      header: 'บัญชีต้นทาง',
      render: (b) => (
        <div>
          {bankNameMap[b.bank_id] && <p className="text-xs text-gray-500 dark:text-slate-400 mb-0.5">{bankNameMap[b.bank_id]}</p>}
          <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded font-mono">{b.from_account || '-'}</code>
        </div>
      ),
    },
    {
      key: 'triggered_by_name',
      header: 'ผู้สั่ง',
      render: (b) => <span className="text-xs text-gray-600 dark:text-slate-300">{b.triggered_by_name || (b.source === 'auto' ? 'ระบบ' : '-')}</span>,
    },
    {
      key: 'created_at',
      header: 'เวลา',
      render: (b) => (
        <div>
          <p className="text-xs text-gray-700 dark:text-slate-300">{dayjs(b.created_at).tz(TZ).format('DD/MM/YY HH:mm:ss')}</p>
          {b.finished_at && <p className="text-[10px] text-gray-400 dark:text-slate-500">จบ {dayjs(b.finished_at).tz(TZ).format('HH:mm:ss')}</p>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (b) => (b.status === 'unknown' || b.status === 'submitting') ? (
        <button
          type="button"
          onClick={() => openReconcileModal(b)}
          className={`${TABLE_ACTION_BTN} bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 flex items-center justify-center gap-1.5 whitespace-nowrap mx-auto`}
          title="ตรวจผลกับ KTB แล้วตัดสินว่าปิดเป็น completed หรือคืน pending"
        >
          <FontAwesomeIcon icon={faQuestionCircle} />
          ตรวจกับธนาคาร
        </button>
      ) : null,
    },
  ], [expandedBatchId, bankNameMap, openReconcileModal]);

  const autoBatchItemColumns: Column<BulkBatchItem>[] = useMemo(() => [
    {
      key: 'select',
      header: '',
      width: '36px',
      className: 'text-center',
      render: (it) => (it.result === 'failed' || it.result === 'skipped') ? (
        <input
          type="checkbox"
          checked={releaseSelectedIds.has(it.id)}
          disabled={!expandedBatchClosed}
          onChange={() => toggleReleaseSelect(it.id)}
          className="w-4 h-4 rounded text-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
          title={expandedBatchClosed ? 'เลือกเพื่อปล่อยให้บอทหยิบใหม่' : 'ปล่อยได้เมื่อ batch ปิดแล้ว'}
        />
      ) : null,
    },
    {
      key: 'payout_number',
      header: 'เลขที่ Payout',
      render: (it) => <span className="font-medium text-gray-800 dark:text-slate-100">{it.payout?.payout_number || it.payout_id}</span>,
    },
    {
      key: 'merchant',
      header: 'ร้านค้า',
      render: (it) => <span className="text-xs text-gray-600 dark:text-slate-300">{it.payout?.merchant?.name || '-'}</span>,
    },
    {
      key: 'bank',
      header: 'ธนาคาร / เลขบัญชี',
      render: (it) => (
        <div>
          <p className="text-xs text-gray-700 dark:text-slate-300">{it.payout?.bank_name || it.recipient?.bank_code || '-'}</p>
          <code className="text-xs text-gray-500 dark:text-slate-400 font-mono">{it.payout?.bank_account_number || it.recipient?.bank_account || '-'}</code>
        </div>
      ),
    },
    {
      key: 'account_name',
      header: 'ชื่อบัญชี',
      render: (it) => <span className="text-xs text-gray-600 dark:text-slate-300">{it.payout?.bank_account_name || '-'}</span>,
    },
    {
      key: 'amount',
      header: 'ยอด',
      className: 'text-right',
      headerClassName: 'text-right',
      render: (it) => <span className="font-semibold text-gray-800 dark:text-slate-100">{formatBaht(it.amount)}</span>,
    },
    {
      key: 'result',
      header: 'ผล',
      className: 'text-center',
      headerClassName: 'text-center',
      render: (it) => (
        <div className="flex flex-col items-center gap-0.5">
          <StatusBadge status={it.result} statusMap={BATCH_ITEM_RESULT_MAP} pulse={it.result === 'unknown'} />
          {it.result === 'released' && it.released_by_name && (
            <span className="text-[10px] text-gray-400 dark:text-slate-500">โดย {it.released_by_name}{it.released_at ? ` · ${dayjs(it.released_at).tz(TZ).format('DD/MM HH:mm')}` : ''}</span>
          )}
        </div>
      ),
    },
    {
      key: 'detail',
      header: 'รายละเอียด',
      render: (it) => {
        const ktbStatus = it.ktb_item?.status;
        const ktbId = it.ktb_item?.bulkItemId;
        if (!it.error && !ktbStatus) return <span className="text-xs text-gray-400 dark:text-slate-500">-</span>;
        return (
          <div className="space-y-0.5 max-w-[260px]">
            {it.error && <p className="text-xs text-red-600 dark:text-red-400 break-words">{it.error}</p>}
            {ktbStatus && ktbStatus !== it.error && <p className="text-[11px] text-gray-500 dark:text-slate-400">KTB: {ktbStatus}</p>}
            {ktbId && <code className="text-[10px] font-mono text-gray-400 dark:text-slate-500">{ktbId}</code>}
          </div>
        );
      },
    },
  ], [releaseSelectedIds, expandedBatchClosed]);

  const releasableCount = expandedBatch
    ? expandedBatch.items.filter(i => i.result === 'failed' || i.result === 'skipped').length
    : 0;

  const autoEnabled = !!autoSettings?.enabled;
  const autoCountdown = (() => {
    if (!autoEnabled) return '—';
    if (autoState.paused_reason) return 'หยุดพัก';
    if (!autoState.next_run_at) return 'รอกำหนดรอบ';
    const diff = dayjs(autoState.next_run_at).valueOf() - (Date.now() + autoClockOffsetRef.current);
    return diff <= 0 ? 'ถึงรอบแล้ว — รอบอทหยิบ' : formatCountdown(diff);
  })();
  const limitHint = (key: keyof AutoBulkLimits, unit: string) =>
    autoLimits ? `${autoLimits[key].min.toLocaleString()} – ${autoLimits[key].max.toLocaleString()} ${unit}` : unit;
  const reconcileCanDecide = !!reconcileBatch
    && BATCH_RECONCILABLE_STATUSES.includes(reconcileBatch.status)
    && !reconcileResult?.auto?.resolved;

  return (
    <div className="max-w-full">

      <UiModal
        open={showModeModal}
        onClose={() => {}}
        title="เลือกโหมดการโอนเงิน"
        icon={faLayerGroup}
        iconColor="text-blue-500"
        size="lg"
      >
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">กรุณาเลือกวิธีการโอนเงินก่อนดำเนินการ</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => { setTransferMode('ktb'); setShowModeModal(false); }}
            className="flex flex-row sm:flex-col items-center gap-4 sm:gap-3 p-4 sm:p-5 rounded-xl border-2 border-blue-200 dark:border-blue-800 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all group text-left sm:text-center"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 flex items-center justify-center transition-colors">
              <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-xl sm:text-2xl text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100">KTB อัตโนมัติ</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">โอนผ่าน KTB Business API</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setTransferMode('ktb_export'); setShowModeModal(false); }}
            className="flex flex-row sm:flex-col items-center gap-4 sm:gap-3 p-4 sm:p-5 rounded-xl border-2 border-emerald-200 dark:border-emerald-800 hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all group text-left sm:text-center"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-900/30 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/50 flex items-center justify-center transition-colors">
              <FontAwesomeIcon icon={faHistory} className="text-xl sm:text-2xl text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100">Export KTB Bulk</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">ส่งออกไฟล์สำหรับ KTB Business</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setTransferMode('scb'); setShowModeModal(false); }}
            className="flex flex-row sm:flex-col items-center gap-4 sm:gap-3 p-4 sm:p-5 rounded-xl border-2 border-purple-200 dark:border-purple-800 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all group text-left sm:text-center"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-purple-100 dark:bg-purple-900/30 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 flex items-center justify-center transition-colors">
              <FontAwesomeIcon icon={faHistory} className="text-xl sm:text-2xl text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100">Export SCB Anywhere</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">ส่งออกไฟล์สำหรับ SCB Anywhere</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => { setTransferMode('ktb_auto'); setShowModeModal(false); clearSelection(); }}
            className="flex flex-row sm:flex-col items-center gap-4 sm:gap-3 p-4 sm:p-5 rounded-xl border-2 border-orange-200 dark:border-orange-800 hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all group text-left sm:text-center"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-full bg-orange-100 dark:bg-orange-900/30 group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50 flex items-center justify-center transition-colors">
              <FontAwesomeIcon icon={faRobot} className="text-xl sm:text-2xl text-orange-600 dark:text-orange-400" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100">KTB อัตโนมัติ (บอท)</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">ให้ระบบหยิบรายการค้างแล้วโอนเองเป็นรอบ ๆ</p>
            </div>
          </button>
        </div>
      </UiModal>

      <UiPageHeader
        icon={faLayerGroup}
        title={transferMode === 'scb' ? 'Export SCB Anywhere' : transferMode === 'ktb_export' ? 'Export KTB Bulk' : transferMode === 'ktb_auto' ? 'KTB อัตโนมัติ (บอท)' : 'Bulk Transfer Auto'}
        subtitle={transferMode === 'scb' ? 'Export ไฟล์สำหรับอัพโหลด SCB Anywhere' : transferMode === 'ktb_export' ? 'Export ไฟล์สำหรับอัพโหลด KTB Business' : transferMode === 'ktb_auto' ? 'ให้ระบบหยิบรายการค้างแล้วโอนเองเป็นรอบ ๆ ผ่าน KTB Business API' : `โอนเงินหลายรายการพร้อมกัน • ${totalItems.toLocaleString()} รายการ`}
        below={
          <div className="flex items-center gap-2 flex-wrap">
            {transferMode && (
              <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${transferMode === 'scb' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : transferMode === 'ktb_export' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : transferMode === 'ktb_auto' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}>
                {transferMode === 'scb' ? 'SCB' : transferMode === 'ktb_export' ? 'KTB Export' : transferMode === 'ktb_auto' ? 'KTB บอท' : 'KTB'}
              </span>
            )}
            {isAutoMode && autoSettings && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${autoEnabled ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${autoEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                {autoEnabled ? 'บอทเปิดอยู่' : 'บอทปิดอยู่'}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowModeModal(true)}
              className={HEADER_GLASS_BTN}
            >
              เปลี่ยนโหมด
            </button>
          </div>
        }
        actions={
          selectedIds.size > 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              {transferMode === 'scb' ? (
                <UiButton
                  icon={faHistory}
                  onClick={() => setShowScbExportModal(true)}
                  disabled={!canApprove}
                  className="!bg-purple-600 hover:!bg-purple-700"
                >
                  Export ({selectedIds.size})
                </UiButton>
              ) : transferMode === 'ktb_export' ? (
                <UiButton
                  icon={faHistory}
                  onClick={() => setShowKtbExportModal(true)}
                  disabled={!canApprove}
                  className="!bg-emerald-600 hover:!bg-emerald-700"
                >
                  Export ({selectedIds.size})
                </UiButton>
              ) : (
                <UiButton
                  icon={faArrowRightArrowLeft}
                  onClick={openTransferModal}
                  disabled={!canApprove || isOverLimit}
                >
                  โอนเงิน ({selectedIds.size})
                </UiButton>
              )}
              <button type="button" onClick={clearSelection} className={HEADER_GLASS_BTN}>
                ล้างการเลือก
              </button>
            </div>
          ) : undefined
        }
      />

      {selectedIds.size > 0 && (selectedIds.size > MAX_SELECT || totalSelected > MAX_AMOUNT) && (
        <div className="mb-4 space-y-2">
          {selectedIds.size > MAX_SELECT && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>เลือกเกิน {MAX_SELECT} รายการ กรุณาลดจำนวนก่อนโอน</span>
            </div>
          )}
          {totalSelected > MAX_AMOUNT && (
            <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>ยอดรวม ฿{totalSelected.toLocaleString()} เกินวงเงิน ฿{MAX_AMOUNT.toLocaleString()} กรุณาลดจำนวนก่อนโอน</span>
            </div>
          )}
        </div>
      )}

      {isAutoMode && (
        <div className="space-y-6 mb-6">
          {/* ⏸️ บอทหยุดพักเอง (batch unknown / error ติดกัน) — ต้องเคลียร์ก่อนบอทถึงจะไปต่อ */}
          {autoState.paused_reason && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 rounded-xl px-4 py-3">
              <div className="flex items-start gap-2 flex-1 text-sm text-red-700 dark:text-red-300">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0 animate-pulse" />
                <div>
                  <p className="font-semibold">บอทหยุดพักอัตโนมัติ — จะไม่เริ่มรอบใหม่จนกว่าจะเคลียร์</p>
                  <p className="text-xs mt-0.5 break-words">{autoState.paused_reason}</p>
                </div>
              </div>
              <UiButton variant="danger" icon={faQuestionCircle} onClick={jumpToUnknownBatches} className="shrink-0 justify-center">
                ดูรายการที่ต้องตรวจสอบ
              </UiButton>
            </div>
          )}

          {/* ⚙️ ตั้งค่าบอท */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faRobot} className="text-orange-500 dark:text-orange-400" />
                ตั้งค่าบอทโอนอัตโนมัติ
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">เปิดบอท</span>
                <UiToggle
                  checked={autoEnabled}
                  onChange={handleAutoToggle}
                  disabled={!autoSettings || autoToggling || !canApprove}
                  ariaLabel="เปิดบอท"
                />
                <span className={`text-xs font-semibold ${autoEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'}`}>
                  {autoToggling ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : autoEnabled ? 'เปิดอยู่' : 'ปิดอยู่'}
                </span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {autoSettingsLoading && !autoSettings ? (
                <div className="py-8 text-center text-gray-400 dark:text-slate-500 text-sm">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  กำลังโหลดการตั้งค่า...
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <UiSelect
                      label="ธนาคารต้นทาง (KTB)"
                      icon={faUniversity}
                      value={autoForm.bankId}
                      onChange={(e) => {
                        const bankId = e.target.value;
                        setAutoForm(f => ({ ...f, bankId, fromAccount: bankId === f.bankId ? f.fromAccount : '' }));
                      }}
                      hint={autoKtbBanks.length === 0 ? 'ไม่พบธนาคาร KTB ที่เปิดใช้งาน — ไปเพิ่มที่หน้าจัดการธนาคารก่อน' : 'เฉพาะบัญชี KTB ที่ผูก credential KTB Business ไว้'}
                    >
                      <option value="">— เลือกธนาคาร —</option>
                      {autoKtbBanks.map((b: any) => (
                        <option key={b.id} value={b.id}>
                          {b.name}{b.account_number ? ` (${b.account_number})` : ''}
                        </option>
                      ))}
                    </UiSelect>
                    <div>
                      <UiInput
                        label="บัญชีต้นทางที่ใช้โอน (from_account)"
                        icon={faMoneyBillWave}
                        value={autoForm.fromAccount}
                        onChange={(e) => setAutoForm(f => ({ ...f, fromAccount: e.target.value }))}
                        placeholder="เลขบัญชี KTB ที่บอทจะใช้โอน"
                        hint="บอทจะโอนจากบัญชีนี้ทุกรอบ ตรวจสอบก่อนเปิด"
                        className="font-mono"
                      />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <UiButton
                          variant="secondary"
                          icon={faDownload}
                          onClick={handleLoadKtbAccounts}
                          loading={autoAccountsLoading}
                          disabled={!autoForm.bankId}
                        >
                          โหลดบัญชีจาก KTB
                        </UiButton>
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                          กดแล้วระบบจะล็อกอิน KTB Business จริง (อาจมี OTP)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <UiInput
                      type="number"
                      label="รอบถี่สุด (นาที)"
                      icon={faClock}
                      value={autoForm.intervalMin}
                      onChange={(e) => setAutoForm(f => ({ ...f, intervalMin: e.target.value }))}
                      hint={limitHint('intervalMin', 'นาที')}
                      min={autoLimits?.intervalMin.min}
                      max={autoLimits?.intervalMin.max}
                    />
                    <UiInput
                      type="number"
                      label="รอบห่างสุด (นาที)"
                      icon={faClock}
                      value={autoForm.intervalMax}
                      onChange={(e) => setAutoForm(f => ({ ...f, intervalMax: e.target.value }))}
                      hint={limitHint('intervalMax', 'นาที')}
                      min={autoLimits?.intervalMax.min}
                      max={autoLimits?.intervalMax.max}
                    />
                    <UiInput
                      type="number"
                      label="สูงสุดต่อรอบ (รายการ)"
                      icon={faLayerGroup}
                      value={autoForm.maxItems}
                      onChange={(e) => setAutoForm(f => ({ ...f, maxItems: e.target.value }))}
                      hint={limitHint('maxItems', 'รายการ')}
                      min={autoLimits?.maxItems.min}
                      max={autoLimits?.maxItems.max}
                    />
                    <UiInput
                      type="number"
                      label="ยอดรวมสูงสุดต่อรอบ"
                      icon={faMoneyBillWave}
                      prefix="฿"
                      value={autoForm.maxTotal}
                      onChange={(e) => setAutoForm(f => ({ ...f, maxTotal: e.target.value }))}
                      hint={limitHint('maxTotal', 'บาท')}
                      min={autoLimits?.maxTotal.min}
                      max={autoLimits?.maxTotal.max}
                    />
                  </div>

                  <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
                    <span>
                      บอทจะสุ่มเวลารอบถัดไประหว่างช่วงถี่สุด–ห่างสุด แล้วหยิบ payout ที่ค้าง (ไม่เกินจำนวน/ยอดที่ตั้ง) ไปโอนผ่าน KTB Business API เอง{' '}
                      <strong>บอทจะโอนจากบัญชีนี้ทุกรอบ ตรวจสอบก่อนเปิด</strong>
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs text-gray-400 dark:text-slate-500">
                      {autoBank
                        ? <>บันทึกไว้: <strong className="text-gray-600 dark:text-slate-300">{autoBank.name}</strong>{autoBank.account_number ? ` (${autoBank.account_number})` : ''} · from_account <code className="font-mono">{autoSettings?.fromAccount || '-'}</code></>
                        : 'ยังไม่ได้ผูกธนาคาร — เปิดบอทไม่ได้จนกว่าจะบันทึกธนาคาร + บัญชีต้นทาง'}
                    </span>
                    <UiButton icon={faSave} onClick={handleAutoSave} loading={autoSaving} disabled={!canApprove || !autoSettings}>
                      บันทึกการตั้งค่า
                    </UiButton>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 📡 สถานะสด */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${autoEnabled ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>
                <FontAwesomeIcon icon={faPowerOff} />
                {autoEnabled ? 'บอทเปิดอยู่' : 'บอทปิดอยู่'}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${autoState.paused_reason ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : autoEnabled ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}>
                <FontAwesomeIcon icon={faClock} />
                รอบถัดไปใน <span className="font-mono tabular-nums">{autoCountdown}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                <FontAwesomeIcon icon={faHistory} />
                รอบล่าสุด {formatDateTime(autoState.last_run_at)}
              </span>
              {autoStatus?.server_time && (
                <span className="text-[11px] text-gray-400 dark:text-slate-500">
                  ช่วงรอบ {autoState.interval_min ?? autoSettings?.intervalMin ?? '-'}–{autoState.interval_max ?? autoSettings?.intervalMax ?? '-'} นาที · sync {dayjs(autoStatus.server_time).tz(TZ).format('HH:mm:ss')}
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                <UiButton
                  icon={faPlay}
                  onClick={handleRunNow}
                  loading={autoRunning}
                  disabled={!autoEnabled || !canApprove}
                  className="!bg-orange-600 hover:!bg-orange-700"
                >
                  รันตอนนี้
                </UiButton>
                <UiButton variant="secondary" icon={faRotate} onClick={() => { loadAutoStatus(false); loadAutoBatches(); }} loading={autoStatusLoading}>
                  รีเฟรช
                </UiButton>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400 break-words">
              <span className="font-semibold text-gray-600 dark:text-slate-300">ผลรอบล่าสุด:</span> {summarizeLastResult(autoState.last_result)}
            </p>
            {!!autoStatus?.blocking_batches?.length && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                <p className="font-semibold mb-1.5">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                  batch ที่ยังไม่ปิด ({autoStatus.blocking_batches.length}) — บอทจะไม่เริ่มรอบใหม่จนกว่าจะปิดครบ
                </p>
                <ul className="space-y-1">
                  {autoStatus.blocking_batches.map(b => (
                    <li key={b.id} className="flex flex-wrap items-center gap-2">
                      <code className="font-mono bg-white/60 dark:bg-slate-800/60 px-1.5 py-0.5 rounded">{b.batch_ref}</code>
                      <StatusBadge status={b.status} statusMap={BATCH_STATUS_MAP} pulse={b.status === 'unknown'} />
                      <span>{b.source === 'auto' ? 'บอท' : 'กดเอง'} · {b.payout_count} รายการ · {formatBaht(b.total_amount)}</span>
                      <span className="text-amber-500 dark:text-amber-400">{formatDateTime(b.submitted_at || b.created_at)}</span>
                      {(b.status === 'unknown' || b.status === 'submitting') && (
                        <button
                          type="button"
                          onClick={() => openReconcileModal(autoBatches.find(x => x.id === b.id) || { ...b, source: b.source as BulkBatch['source'], status: b.status as BulkBatchStatus })}
                          className={`${TABLE_ACTION_BTN} bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800`}
                        >
                          ตรวจกับธนาคาร
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* 📜 ประวัติ batch (บอท + กดเอง) */}
          <div ref={autoHistoryRef} className="space-y-4 scroll-mt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faHistory} className="text-orange-500 dark:text-orange-400" />
                ประวัติ Bulk Batch (บอท + กดเอง)
                {autoBatchStatusFilter === 'unknown' && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 animate-pulse">
                    กรองเฉพาะที่ต้องตรวจสอบ
                  </span>
                )}
              </h3>
              <UiButton variant="secondary" icon={faRotate} onClick={loadAutoBatches} loading={autoBatchesLoading}>
                Refresh
              </UiButton>
            </div>

            <UiFilterPanel
              dateFrom={autoBatchStartDate}
              dateTo={autoBatchEndDate}
              onDateFromChange={setAutoBatchStartDate}
              onDateToChange={setAutoBatchEndDate}
              onSearch={handleAutoBatchSearch}
              onReset={handleAutoBatchReset}
              loading={autoBatchesLoading}
            >
              <div className="w-full sm:w-[180px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  <FontAwesomeIcon icon={faFilter} className="mr-1" />
                  สถานะ
                </label>
                <select
                  value={autoBatchStatusFilter}
                  onChange={(e) => { setAutoBatchStatusFilter(e.target.value); setAutoBatchesPage(1); }}
                  className={FILTER_SELECT_CLASS}
                >
                  <option value="">ทั้งหมด</option>
                  {Object.entries(BATCH_STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-[150px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  <FontAwesomeIcon icon={faRobot} className="mr-1" />
                  แหล่ง
                </label>
                <select
                  value={autoBatchSourceFilter}
                  onChange={(e) => { setAutoBatchSourceFilter(e.target.value); setAutoBatchesPage(1); }}
                  className={FILTER_SELECT_CLASS}
                >
                  <option value="">ทั้งหมด</option>
                  <option value="auto">บอท</option>
                  <option value="manual">กดเอง</option>
                </select>
              </div>
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                  ค้นหา
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Batch Ref, Bulk Order ID, Payout..."
                    value={autoBatchSearchInput}
                    onChange={(e) => setAutoBatchSearchInput(e.target.value)}
                    className={SEARCH_INPUT_CLASS}
                  />
                  {autoBatchSearchInput && (
                    <button
                      type="button"
                      onClick={() => setAutoBatchSearchInput('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-xs" />
                    </button>
                  )}
                </div>
              </div>
            </UiFilterPanel>

            <DataTable
              data={autoBatches}
              columns={autoBatchColumns}
              loading={autoBatchesLoading}
              keyExtractor={(b) => b.id}
              emptyMessage="ยังไม่มี batch ในช่วงที่เลือก"
              emptyIcon={<FontAwesomeIcon icon={faRobot} className="text-5xl text-gray-200 dark:text-slate-600" />}
              compact
              hoverable
              rowClassName={(b) => expandedBatchId === b.id
                ? 'bg-orange-50/50 dark:bg-orange-900/10'
                : b.status === 'unknown' ? 'bg-red-50/40 dark:bg-red-900/10' : ''}
              serverPagination={{
                currentPage: autoBatchesPage,
                totalItems: autoBatchesTotal,
                pageSize: AUTO_BATCH_PAGE_SIZE,
                pageSizeOptions: [AUTO_BATCH_PAGE_SIZE],
                onPageChange: setAutoBatchesPage,
              }}
            />

            {expandedBatch && (
              <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-gray-600 dark:text-slate-300 space-y-0.5">
                    <p className="font-semibold">
                      รายการใน Batch {expandedBatch.batch_ref} ({expandedBatch.items.length} รายการ)
                    </p>
                    {expandedBatch.error_message && (
                      <p className="text-red-600 dark:text-red-400 break-words">ข้อผิดพลาด: {expandedBatch.error_message}</p>
                    )}
                    {expandedBatch.reconciled_at && (
                      <p className="text-gray-500 dark:text-slate-400">
                        Reconcile โดย {expandedBatch.reconciled_by_name || '-'} · {formatDateTime(expandedBatch.reconciled_at)}{expandedBatch.reconcile_note ? ` · ${expandedBatch.reconcile_note}` : ''}
                      </p>
                    )}
                    {releasableCount > 0 && !expandedBatchClosed && (
                      <p className="text-amber-600 dark:text-amber-400">ปล่อยรายการได้เมื่อ batch ปิดแล้วเท่านั้น (ตอนนี้ยัง {BATCH_STATUS_MAP[expandedBatch.status]?.label || expandedBatch.status})</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {releasableCount > 0 && (
                      <UiButton
                        variant="secondary"
                        icon={faUnlock}
                        onClick={() => handleReleaseItems(expandedBatch)}
                        loading={releasing}
                        disabled={!expandedBatchClosed || releaseSelectedIds.size === 0 || !canApprove}
                        className="!text-orange-700 dark:!text-orange-300 !border-orange-200 dark:!border-orange-800 !bg-orange-50 dark:!bg-orange-900/20 hover:!bg-orange-100 dark:hover:!bg-orange-900/30"
                      >
                        ปล่อยให้บอทหยิบใหม่{releaseSelectedIds.size > 0 ? ` (${releaseSelectedIds.size})` : ''}
                      </UiButton>
                    )}
                    <button
                      type="button"
                      onClick={() => { setExpandedBatchId(null); setReleaseSelectedIds(new Set()); }}
                      className="text-xs text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTimes} className="mr-1" />
                      ปิด
                    </button>
                  </div>
                </div>
                <DataTable
                  data={expandedBatch.items}
                  columns={autoBatchItemColumns}
                  keyExtractor={(it) => it.id}
                  emptyMessage="batch นี้ไม่มีรายการ"
                  compact
                  hoverable
                  className="rounded-none shadow-none"
                  pagination={false}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!isAutoMode && (<>
      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleFilterReset}
        loading={loading}
        actions={
          <>
            <UiButton variant="secondary" icon={faRotate} onClick={() => loadData()}>
              Refresh
            </UiButton>
            {canApprove && selectedIds.size > 0 && (
              transferMode === 'scb' ? (
                <UiButton
                  icon={faHistory}
                  onClick={() => setShowScbExportModal(true)}
                  className="!bg-purple-600 hover:!bg-purple-700"
                >
                  Export ({selectedIds.size})
                </UiButton>
              ) : transferMode === 'ktb_export' ? (
                <UiButton
                  icon={faHistory}
                  onClick={() => setShowKtbExportModal(true)}
                  className="!bg-emerald-600 hover:!bg-emerald-700"
                >
                  Export ({selectedIds.size})
                </UiButton>
              ) : (
                <UiButton
                  icon={faArrowRightArrowLeft}
                  onClick={openTransferModal}
                  disabled={isOverLimit}
                >
                  โอนเงิน ({selectedIds.size})
                </UiButton>
              )
            )}
          </>
        }
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="เลข Payout, ข้อมูลบัญชี..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={SEARCH_INPUT_CLASS}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faFilter} className="mr-1" />
            Merchant
          </label>
          <MerchantMultiSelect
            merchants={merchants}
            value={selectedMerchantId}
            onChange={(v) => { setSelectedMerchantId(v); setCurrentPage(1); }}
            variant="light"
          />
        </div>

        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">เรียงลำดับ</label>
          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value as 'desc' | 'asc'); setCurrentPage(1); }}
            className={FILTER_SELECT_CLASS}
          >
            <option value="desc">ล่าสุดก่อน</option>
            <option value="asc">เก่าสุดก่อน</option>
          </select>
        </div>

        <div className="min-w-[280px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faMoneyBillWave} className="mr-1" />
            ช่วงยอด (ไม่ติ๊ก = ทั้งหมด)
          </label>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2">
            {AMOUNT_RANGES.map(r => (
              <label key={r.key} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={amountRanges.has(r.key)}
                  onChange={() => toggleAmountRange(r.key)}
                  className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                />
                <span className="text-xs text-gray-600 dark:text-slate-300 whitespace-nowrap tabular-nums">{r.label}</span>
              </label>
            ))}
          </div>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading && !summaryTotal} cols={4} />

      {transferMode === 'ktb_export' && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4 mb-4 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-300">
          <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 shrink-0" />
          <span>โหมดนี้สร้างไฟล์ KTB Bulk Transfer สำหรับอัพโหลดเข้า KTB Business เอง</span>
        </div>
      )}

      {transferMode === 'scb' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-theme mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-600 dark:text-slate-300">กรองปลายทาง:</span>
          <div className="flex bg-gray-100 dark:bg-slate-700 rounded-lg p-1 gap-1">
            <button
              type="button"
              onClick={() => { setScbFilter('all'); clearSelection(); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${scbFilter === 'all' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-100'}`}
            >
              <FontAwesomeIcon icon={faLayerGroup} className="mr-1.5" />
              ทั้งหมด
            </button>
            <button
              type="button"
              onClick={() => { setScbFilter('scb'); clearSelection(); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${scbFilter === 'scb' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-100'}`}
            >
              <FontAwesomeIcon icon={faUniversity} className="mr-1.5" />
              โอนไปยัง SCB
            </button>
            <button
              type="button"
              onClick={() => { setScbFilter('other'); clearSelection(); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${scbFilter === 'other' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-slate-100'}`}
            >
              <FontAwesomeIcon icon={faUniversity} className="mr-1.5" />
              โอนไปยังธนาคารอื่นๆ
            </button>
          </div>
          <span className="text-xs text-gray-400 dark:text-slate-500">
            {scbFilter === 'scb'
              ? 'แสดงเฉพาะบัญชี SCB ไทยพาณิชย์'
              : scbFilter === 'other'
                ? 'แสดงเฉพาะบัญชีธนาคารอื่น (ไม่รวม SCB)'
                : 'แสดงทุกรายการ — อัพโหลดไฟล์เดียวสำหรับทุกธนาคาร'}
          </span>
          {(scbFilter === 'all' || scbFilter === 'other') && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none ml-2">
              <input
                type="checkbox"
                checked={excludeBBL}
                onChange={e => { setExcludeBBL(e.target.checked); clearSelection(); }}
                className="w-4 h-4 rounded text-blue-600 cursor-pointer"
              />
              <span className="text-xs text-gray-600 dark:text-slate-300">กรองธนาคารกรุงเทพออก</span>
            </label>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={pageAllSelected}
                    onChange={toggleSelectAll}
                    disabled={loading}
                    className="w-4 h-4 rounded text-blue-600 cursor-pointer disabled:opacity-40"
                    title="เลือกทั้งหน้า"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">เลขที่ Payout</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Merchant</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">บัญชีปลายทาง</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">จำนวนเงิน</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">วันที่</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">ข้อผิดพลาด</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {loading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-3"><div className="w-4 h-4 bg-gray-200 dark:bg-slate-600 rounded" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-32" /></td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-28 mb-1" />
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-20" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-gray-200 dark:bg-slate-600 rounded w-16 mb-1" />
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-24 mb-1" />
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-20" />
                    </td>
                    <td className="px-4 py-3 text-right"><div className="h-5 bg-gray-200 dark:bg-slate-600 rounded w-20 ml-auto" /></td>
                    <td className="px-4 py-3">
                      <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-20 mb-1" />
                      <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-12" />
                    </td>
                    <td className="px-4 py-3"><div className="h-3 bg-gray-100 dark:bg-slate-700 rounded w-28" /></td>
                  </tr>
                ))
              ) : displayedPayouts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-16 text-center text-gray-400 dark:text-slate-500">
                    <FontAwesomeIcon icon={faArrowRightArrowLeft} className="text-4xl mb-3 text-gray-300 dark:text-slate-600 block mx-auto" />
                    {transferMode === 'scb' && payouts.length > 0
                      ? `ไม่มีรายการที่${scbFilter === 'scb' ? 'เป็นธนาคาร SCB' : scbFilter === 'other' ? 'ไม่ใช่ธนาคาร SCB' : 'ตรงกับเงื่อนไข'}`
                      : 'ไม่มีรายการ Payout'}
                  </td>
                </tr>
              ) : (
                displayedPayouts.map((payout) => {
                  const isSelected = selectedIds.has(payout.id);
                  const isBusyPayout = busyPayoutIds.has(payout.id);
                  const tr = payout.transfer_response;
                  const isUnknown = !!tr;
                  let transferError: string | null = null;
                  if (tr) {
                    if (typeof tr.raw_data === 'string') transferError = tr.raw_data;
                    else if (tr.raw_data?.message) transferError = tr.raw_data.message;
                    else if (typeof tr.message === 'string') transferError = tr.message;
                  }
                  return (
                    <tr
                      key={payout.id}
                      onClick={() => !isBusyPayout && toggleSelect(payout)}
                      className={`transition-colors ${isBusyPayout ? 'opacity-50 cursor-not-allowed bg-orange-50 dark:bg-orange-900/20' : `cursor-pointer ${isUnknown ? (isSelected ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'bg-amber-50/60 dark:bg-amber-900/10 hover:bg-amber-100/60 dark:hover:bg-amber-900/20') : isSelected ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}`}
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => !isBusyPayout && toggleSelect(payout)}
                          disabled={isBusyPayout}
                          className="w-4 h-4 rounded text-blue-600 cursor-pointer disabled:cursor-not-allowed"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800 dark:text-slate-100">{payout.payout_number}</p>
                        {isBusyPayout && (
                          <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded text-xs font-medium">
                            <FontAwesomeIcon icon={faSpinner} className="text-xs animate-spin" />
                            กำลังดำเนินการ
                          </span>
                        )}
                        {!isBusyPayout && isUnknown && (
                          <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-medium">
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-xs" />
                            รอตรวจสอบ
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800 dark:text-slate-100">{payout.merchant?.name}</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500">{payout.merchant?.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-gray-500 dark:text-slate-400">{payout.bank_name}</p>
                        <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1.5 py-0.5 rounded">{payout.bank_account_number}</code>
                        <p className="text-xs text-gray-700 dark:text-slate-300 mt-0.5">{payout.bank_account_name}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <p className="font-bold text-base" style={{ color: 'var(--theme-primary)' }}>
                          ฿{payout.amount.toLocaleString()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700 dark:text-slate-300">{new Date(payout.created_at).toLocaleDateString('th-TH')}</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500">
                          {new Date(payout.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </td>
                      <td className="px-4 py-3 max-w-[200px]">
                        {transferError && (
                          <div title={transferError} className="flex items-start gap-1">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-400 dark:text-orange-500 text-xs mt-0.5 shrink-0" />
                            <p className="text-xs text-orange-600 dark:text-orange-400 leading-tight line-clamp-2 break-all">
                              {transferError}
                            </p>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          showPageSizeSelector={true}
          showInfo={true}
          showFirstLast={true}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        />
      </div>
      </>)}

      {selectedPayouts.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme mb-6">
          <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faCheck} className="text-blue-500 dark:text-blue-400" />
              รายการที่เลือก ({selectedPayouts.length} รายการ)
            </h3>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-bold ${totalSelected > MAX_AMOUNT ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                รวม ฿{totalSelected.toLocaleString()}
                {totalSelected > MAX_AMOUNT && <span className="ml-1 text-xs">(เกินวงเงิน)</span>}
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 px-2 py-1 rounded border border-gray-200 dark:border-slate-600 hover:border-red-300 dark:hover:border-red-700 transition-colors"
              >
                ล้างทั้งหมด
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-slate-700/50">
                <tr className="border-b border-gray-200 dark:border-slate-600">
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">#</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขที่ Payout</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">Merchant</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ธนาคาร / เลขบัญชี</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">จำนวนเงิน</th>
                  <th className="w-8 px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {selectedPayouts.map((payout, i) => (
                  <tr key={payout.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                    <td className="px-4 py-2 text-gray-400 dark:text-slate-500 text-xs">{i + 1}</td>
                    <td className="px-4 py-2 font-medium text-gray-800 dark:text-slate-100">{payout.payout_number}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-slate-300 text-xs">{payout.merchant?.name}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs text-gray-500 dark:text-slate-400">{payout.bank_name} — </span>
                      <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 py-0.5 rounded">{payout.bank_account_number}</code>
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-green-700 dark:text-green-400">
                      ฿{payout.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => toggleSelect(payout)}
                        className="text-gray-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {transferMode !== 'scb' && !isAutoMode && (unknownLoading || unknownGroups.length > 0) && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme overflow-hidden mb-6 border-2 border-amber-200 dark:border-amber-800">
          <div className="p-4 border-b border-amber-100 dark:border-amber-900/50 flex items-center justify-between bg-amber-50 dark:bg-amber-900/20">
            <h3 className="font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <FontAwesomeIcon icon={faQuestionCircle} className="text-amber-500 dark:text-amber-400" />
              รายการที่ต้องตรวจสอบ — ไม่ทราบสถานะการโอน
              {unknownGroups.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 bg-amber-500 dark:bg-amber-600 text-white text-xs font-bold rounded-full">
                  {unknownGroups.length}
                </span>
              )}
            </h3>
            <button
              type="button"
              onClick={loadUnknownGroups}
              className="px-3 py-1.5 text-xs rounded-lg border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center gap-1.5 transition-colors"
            >
              <FontAwesomeIcon icon={faRotate} className={unknownLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          <div className="px-4 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/50">
            <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
              รายการเหล่านี้ถูกส่งโอนเงินแล้ว แต่ยังไม่ทราบผลจากธนาคาร
              หากตรวจสอบแล้วพบว่าเงินออกไปแล้ว ให้กด "ปรับสถานะเป็น completed" เพื่อเปลี่ยนสถานะเป็น completed และส่ง webhook หากยังไม่ออกสามารถเลือกรายการในตารางด้านบนเพื่อทำรายการถอนใหม่อีกครั้ง
            </p>
          </div>

          {unknownLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-400 dark:text-slate-500">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl mr-3" />
              กำลังโหลด...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Bulk Order ID</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">สถานะธนาคาร</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">บัญชีต้นทาง</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">จำนวนรายการ</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">ยอดรวม</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">ผู้ดำเนินการ</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {unknownGroups.map((group) => (
                    <React.Fragment key={group.bulk_order_id}>
                      <tr className="hover:bg-amber-50/30 dark:hover:bg-amber-900/10 transition-colors">
                        <td className="px-4 py-3">
                          <code className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-1 rounded font-mono break-all">
                            {group.bulk_order_id}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                            <FontAwesomeIcon icon={faQuestionCircle} />
                            {group.bulk_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded font-mono">
                            {group.from_account}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-bold border border-amber-200 dark:border-amber-800">
                            {group.item_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-amber-700 dark:text-amber-400">฿{Number(group.total_amount).toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600 dark:text-slate-300">{group.approved_by_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setExpandedUnknownId(expandedUnknownId === group.bulk_order_id ? null : group.bulk_order_id)}
                              className={`${TABLE_ACTION_BTN} border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-slate-300`}
                              title="ดูรายการ"
                            >
                              <FontAwesomeIcon icon={expandedUnknownId === group.bulk_order_id ? faChevronUp : faChevronDown} />
                            </button>
                            {canApprove && (
                              <button
                                type="button"
                                onClick={() => setConfirmGroup(group)}
                                disabled={completingBulkId === group.bulk_order_id}
                                className={`${TABLE_ACTION_BTN} bg-green-600 hover:bg-green-700 text-white border-transparent disabled:opacity-50 flex items-center gap-1.5`}
                                title="ยืนยันโอนสำเร็จ"
                              >
                                {completingBulkId === group.bulk_order_id ? (
                                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                ) : (
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                )}
                                ปรับสถานะเป็น completed
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {expandedUnknownId === group.bulk_order_id && (
                        <tr>
                          <td colSpan={7} className="px-4 py-0">
                            <div className="bg-amber-50/40 dark:bg-amber-900/10 rounded-xl my-2 overflow-hidden border border-amber-200 dark:border-amber-800">
                              <div className="px-4 py-2 bg-amber-100/60 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800">
                                <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                                  รายการ Payout ใน Bulk นี้ ({group.items.length} รายการ) — สถานะปัจจุบัน: pending
                                </span>
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-amber-200 dark:border-amber-800">
                                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-slate-400">#</th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-slate-400">เลขที่ Payout</th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-slate-400">ธนาคาร / เลขบัญชี</th>
                                    <th className="text-left px-3 py-2 font-semibold text-gray-500 dark:text-slate-400">ชื่อบัญชี</th>
                                    <th className="text-right px-3 py-2 font-semibold text-gray-500 dark:text-slate-400">จำนวน</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-amber-100 dark:divide-amber-900/30">
                                  {group.items.map((item: any, idx: number) => (
                                    <tr key={item.payout_id} className="hover:bg-white/50 dark:hover:bg-slate-700/30">
                                      <td className="px-3 py-2 text-gray-400 dark:text-slate-500">{idx + 1}</td>
                                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-slate-100">{item.payout_number}</td>
                                      <td className="px-3 py-2">
                                        <span className="text-gray-500 dark:text-slate-400">{item.bank_name} — </span>
                                        <code className="bg-white dark:bg-slate-700 px-1 py-0.5 rounded text-gray-700 dark:text-slate-200">{item.bank_account_number}</code>
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 dark:text-slate-300">{item.bank_account_name}</td>
                                      <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-400">
                                        ฿{Number(item.amount).toLocaleString()}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {transferMode !== 'scb' && !isAutoMode && (
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faHistory} className="text-purple-500 dark:text-purple-400" />
              Bulk Transfer History
            </h3>
            <div className="flex items-center gap-2">
              <UiButton
                variant="secondary"
                icon={faMoneyBillWave}
                onClick={openOrderBulkModal}
                className="!text-green-700 dark:!text-green-400 !border-green-200 dark:!border-green-800 !bg-green-50 dark:!bg-green-900/20 hover:!bg-green-100 dark:hover:!bg-green-900/30"
              >
                ดูรายการบัญชี
              </UiButton>
              <UiButton variant="secondary" icon={faRotate} onClick={loadBulkOrders} loading={bulkOrdersLoading}>
                Refresh
              </UiButton>
            </div>
          </div>

          <UiFilterPanel
            dateFrom={historyStartDate}
            dateTo={historyEndDate}
            onDateFromChange={setHistoryStartDate}
            onDateToChange={setHistoryEndDate}
            onSearch={handleHistorySearch}
            onReset={handleHistoryReset}
            loading={bulkOrdersLoading}
          >
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                ค้นหา
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Order ID, Ref, Payout, บัญชี..."
                  value={historySearchInput}
                  onChange={(e) => setHistorySearchInput(e.target.value)}
                  className={SEARCH_INPUT_CLASS}
                />
                {historySearchInput && (
                  <button
                    type="button"
                    onClick={() => setHistorySearchInput('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
            </div>
          </UiFilterPanel>

          <DataTable
            data={bulkOrders}
            columns={bulkOrderColumns}
            loading={bulkOrdersLoading}
            keyExtractor={(order) => order.bulk_order_id}
            emptyMessage="ยังไม่มีประวัติ Bulk Transfer"
            emptyIcon={<FontAwesomeIcon icon={faHistory} className="text-5xl text-gray-200 dark:text-slate-600" />}
            compact
            hoverable
            rowClassName={(order) => expandedOrderId === order.bulk_order_id ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}
            serverPagination={{
              currentPage: bulkOrdersPage,
              totalItems: bulkOrdersTotal,
              pageSize: bulkOrdersPageSize,
              pageSizeOptions: [10],
              onPageChange: setBulkOrdersPage,
            }}
          />

          {expandedBulkOrder && (
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                  รายการ Payout ใน Bulk Order นี้ ({expandedBulkOrder.items.length} รายการ)
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedOrderId(null)}
                  className="text-xs text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  ปิด
                </button>
              </div>
              <DataTable
                data={expandedBulkOrder.items}
                columns={bulkOrderItemColumns}
                keyExtractor={(item) => item.payout_id}
                compact
                hoverable
                className="rounded-none shadow-none"
                pagination={false}
              />
            </div>
          )}
        </div>
      )}

      {/* Bulk Status Modal */}
      {showStatusModal && bulkStatusResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowStatusModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faEye} className="text-blue-500" />
                สถานะ Bulk Transfer (จากธนาคาร)
              </h2>
              <button onClick={() => setShowStatusModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-sm">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">สถานะ</p>
                  <div className="mt-1">{getBulkStatusBadge(bulkStatusResult.bulk_status)}</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">ยอดรวม</p>
                  <p className="font-bold text-gray-800">฿{Number(bulkStatusResult.total_amount || 0).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">ค่าธรรมเนียม</p>
                  <p className="font-bold text-gray-800">฿{Number(bulkStatusResult.total_fee || 0).toLocaleString()}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Ref No.</p>
                  <p className="font-bold text-gray-800 text-xs break-all">{bulkStatusResult.package_ref_no || '-'}</p>
                </div>
              </div>

              <div className="text-xs text-gray-400">
                Bulk Order ID: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{bulkStatusResult.bulk_order_id}</code>
              </div>

              {/* Items table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">#</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">เลขบัญชี</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">ชื่อบัญชี</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">ธนาคาร</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">จำนวน</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(bulkStatusResult.items || []).map((item, idx) => (
                      <tr key={item.bulkItemId || idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{item.payeeAccountNo}</code>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">{item.payeeAccountName}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{item.payeeBankName}</td>
                        <td className="px-3 py-2 text-right font-semibold text-xs">฿{Number(item.amount).toLocaleString()}</td>
                        <td className="px-3 py-2 text-center">
                          {item.status?.includes('ไม่สำเร็จ') ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                              <FontAwesomeIcon icon={faTimesCircle} /> {item.status}
                            </span>
                          ) : item.status?.includes('สำเร็จ') ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                              <FontAwesomeIcon icon={faCheckCircle} /> {item.status}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                              {item.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setShowStatusModal(false)}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Bulk / Bank Statement Modal */}
      {showOrderBulkModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowOrderBulkModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-500" />
                รายการธนาคาร (Bank Statement)
              </h2>
              <button onClick={() => setShowOrderBulkModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {/* Bank/Account selector */}
              {orderBulkLoadingAccounts ? (
                <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  กำลังโหลดบัญชี...
                </div>
              ) : orderBulkNeedsBankSelection ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">เลือกธนาคาร</label>
                  {orderBulkBanks.map((bank: any) => (
                    <button
                      key={bank.id}
                      type="button"
                      onClick={() => fetchOrderBulkAccounts(bank.id)}
                      className="w-full p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-left transition-all"
                    >
                      <div className="font-medium text-gray-800">{bank.name}</div>
                      <div className="text-sm text-gray-500">{bank.bank_code}</div>
                    </button>
                  ))}
                </div>
              ) : orderBulkAccounts.length > 0 ? (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <FontAwesomeIcon icon={faUniversity} className="mr-1 text-blue-500" />
                      เลือกบัญชี
                    </label>
                    <div className="space-y-2 max-h-36 overflow-y-auto">
                      {orderBulkAccounts.map((account: any) => (
                        <label
                          key={account.accountRefId}
                          className={`block p-3 rounded-xl border-2 cursor-pointer transition-all ${orderBulkSelectedAccountRefId === account.accountRefId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="radio"
                              name="orderBulkAccount"
                              value={account.accountRefId}
                              checked={orderBulkSelectedAccountRefId === account.accountRefId}
                              onChange={() => setOrderBulkSelectedAccountRefId(account.accountRefId)}
                              className="w-4 h-4 text-blue-600"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-800 text-sm">{account.accountNo}</span>
                                <span className="text-sm font-semibold text-green-600">
                                  ฿{account.availableBalance?.toLocaleString()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">{account.accountName}</div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Date + Fetch button */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faCalendarDay} className="text-gray-400 text-sm" />
                      <input
                        type="date"
                        value={orderBulkDate}
                        onChange={(e) => { setOrderBulkDate(e.target.value); setOrderBulkData(null); }}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                    <button
                      onClick={() => fetchOrderBulk(0)}
                      disabled={orderBulkLoading || !orderBulkSelectedAccountRefId}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {orderBulkLoading ? (
                        <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> กำลังดึงข้อมูล...</>
                      ) : (
                        <><FontAwesomeIcon icon={faSearch} /> ดึงรายการ</>
                      )}
                    </button>
                  </div>
                </>
              ) : null}

              {/* Transaction table */}
              {orderBulkData && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>
                      ทั้งหมด {orderBulkData.pageable.totalElements} รายการ
                      • หน้า {orderBulkData.pageable.pageNumber + 1} / {orderBulkData.pageable.totalPages}
                    </span>
                    <span>{orderBulkData.pageable.numberOfElements} รายการในหน้านี้</span>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200 max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 whitespace-nowrap">วันที่/เวลา</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Code</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">รายการ</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">จำนวนเงิน</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500">ยอดคงเหลือ</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">ช่องทาง</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...orderBulkData.content]
                          .sort((a, b) => {
                            if (!a.transactionDateTime) return 1;
                            if (!b.transactionDateTime) return -1;
                            return new Date(b.transactionDateTime).getTime() - new Date(a.transactionDateTime).getTime();
                          })
                          .map((tx, idx) => (
                          <tr key={tx.transactionCode ?? idx} className={`hover:bg-gray-50 ${tx.transferAmount === null ? 'bg-gray-50/50' : ''}`}>
                            <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                              {tx.transactionDateTime
                                ? new Date(tx.transactionDateTime).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })
                                : '-'}
                            </td>
                            <td className="px-3 py-2">
                              {tx.transactionCode ? (
                                <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{tx.transactionCode}</code>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-700">
                              {tx.transactionComment || '-'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {tx.transferAmount !== null ? (
                                <span className={`text-xs font-semibold ${tx.transferAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {tx.transferAmount >= 0 ? '+' : ''}{Number(tx.transferAmount).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-medium text-gray-700">
                              {Number(tx.balance).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">{tx.descriptionChannel || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <Pagination
                    currentPage={orderBulkPage + 1}
                    totalPages={orderBulkData.pageable.totalPages}
                    totalItems={orderBulkData.pageable.totalElements}
                    pageSize={orderBulkData.pageable.pageSize}
                    pageSizeOptions={[orderBulkData.pageable.pageSize]}
                    showPageSizeSelector={false}
                    showInfo={true}
                    showFirstLast={true}
                    onPageChange={(page) => fetchOrderBulk(page - 1)}
                    onPageSizeChange={() => {}}
                  />
                </div>
              )}
            </div>

            <div className="p-5 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setShowOrderBulkModal(false)}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      <UiModal
        open={showTransferModal}
        onClose={() => !transferring && setShowTransferModal(false)}
        title="ยืนยันการโอนเงิน Bulk Transfer"
        icon={faLayerGroup}
        iconColor="text-blue-500"
        size="xl"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowTransferModal(false)} disabled={transferring} className="flex-1 justify-center">
              {transferResult ? 'ปิด' : 'ยกเลิก'}
            </UiButton>
            {!transferResult && (
              <UiButton
                icon={faArrowRightArrowLeft}
                onClick={handleTransfer}
                disabled={transferring || !selectedFromAccount || bankAccounts.length === 0 || busyAccounts.has(selectedFromAccount)}
                loading={transferring}
                className="flex-1 justify-center !bg-blue-600 hover:!bg-blue-700"
              >
                {transferring ? 'กำลังโอนเงิน...' : `ยืนยันโอนเงิน ${selectedPayouts.length} รายการ`}
              </UiButton>
            )}
          </div>
        }
      >
        <div className="space-y-5">
              {/* Transfer Result */}
              {transferResult ? (
                <div className="space-y-4">
                  <div className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm border ${transferResult.failed_count === 0 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}>
                    <FontAwesomeIcon
                      icon={transferResult.failed_count === 0 ? faCheckCircle : faExclamationTriangle}
                      className={transferResult.failed_count === 0 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}
                    />
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-slate-100">
                        {transferResult.failed_count === 0 ? 'โอนเงินสำเร็จทั้งหมด!' : 'โอนเงินบางส่วนสำเร็จ'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        สำเร็จ {transferResult.success_count} รายการ
                        {transferResult.failed_count > 0 && ` | ล้มเหลว ${transferResult.failed_count} รายการ`}
                        {transferResult.bulk_order_id && ` | Order: ${transferResult.bulk_order_id}`}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center text-sm">
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                      <p className="text-gray-500 dark:text-slate-400 text-xs">ยอดรวม</p>
                      <p className="font-bold text-gray-800 dark:text-slate-100">฿{Number(transferResult.total_amount || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                      <p className="text-gray-500 dark:text-slate-400 text-xs">ค่าธรรมเนียม</p>
                      <p className="font-bold text-gray-800 dark:text-slate-100">฿{Number(transferResult.total_fee || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
                      <p className="text-gray-500 dark:text-slate-400 text-xs">สถานะ Bulk</p>
                      <p className="font-bold text-gray-800 dark:text-slate-100 text-xs">{transferResult.bulk_status || '-'}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-600 max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขที่</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขบัญชี</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">จำนวน</th>
                          <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">สถานะ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                        {(transferResult.items || []).map((item: BulkResultItem) => (
                          <tr key={item.payout_id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                            <td className="px-3 py-2 font-medium text-gray-800 dark:text-slate-100 text-xs">{item.payout_number}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-slate-300">
                              <p>{item.payeeAccountNo || item.bank_account_number}</p>
                              {item.payeeAccountName && <p className="text-gray-400 dark:text-slate-500">{item.payeeAccountName}</p>}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-xs text-gray-800 dark:text-slate-100">฿{Number(item.amount).toLocaleString()}</td>
                            <td className="px-3 py-2 text-center">
                              {item.success ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs">
                                  <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                                  {item.status}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-xs">
                                  <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
                                  {item.status}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        สรุปรายการที่จะโอน
                      </span>
                      <span className="text-blue-900 dark:text-blue-200 font-bold">
                        {selectedPayouts.length} รายการ | ฿{totalSelected.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-600 max-h-44 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">#</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขที่ Payout</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ธนาคาร / บัญชี</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                        {selectedPayouts.map((p, i) => (
                          <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                            <td className="px-3 py-2 text-gray-400 dark:text-slate-500 text-xs">{i + 1}</td>
                            <td className="px-3 py-2 font-medium text-gray-800 dark:text-slate-100 text-xs">{p.payout_number}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-slate-300">
                              <span className="text-gray-500 dark:text-slate-400">{p.bank_name}</span>
                              <code className="ml-1 bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 py-0.5 rounded">{p.bank_account_number}</code>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-green-700 dark:text-green-400 text-xs">฿{p.amount.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-3">
                    <label className={LABEL_CLASS}>
                      <FontAwesomeIcon icon={faUniversity} className="mr-1 text-purple-500 dark:text-purple-400" />
                      เลือกธนาคารสำหรับโอนเงิน *
                    </label>

                    {loadingAccounts && (
                      <div className="flex items-center justify-center py-6 text-gray-500 dark:text-slate-400 text-sm">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        กำลังโหลด...
                      </div>
                    )}

                    {!loadingAccounts && needsBankSelection && (
                      <div className="space-y-2">
                        {withdrawBanks.map((bank: any) => (
                          <button
                            key={bank.id}
                            type="button"
                            onClick={() => fetchBankAccounts(bank.id)}
                            className={`w-full p-3 rounded-xl border-2 text-left transition-all ${selectedBankId === bank.id ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-500 hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}
                          >
                            <div className="font-medium text-gray-800 dark:text-slate-100">{bank.name}</div>
                            <div className="text-sm text-gray-500 dark:text-slate-400">{bank.bank_code}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {!loadingAccounts && !needsBankSelection && bankAccounts.length > 0 && (
                      <>
                        {withdrawBanks.length > 1 && (
                          <button
                            type="button"
                            onClick={() => { setNeedsBankSelection(true); setBankAccounts([]); setSelectedFromAccount(''); }}
                            className="text-xs text-purple-600 dark:text-purple-400 hover:underline mb-1"
                          >
                            ← เปลี่ยนธนาคาร
                          </button>
                        )}
                        <div className="space-y-2 max-h-52 overflow-y-auto">
                          {bankAccounts.map((account: any) => {
                            const isBusy = busyAccounts.has(account.accountNo);
                            return (
                              <label
                                key={account.accountRefId}
                                className={`block p-3 rounded-xl border-2 transition-all ${
                                  isBusy
                                    ? 'opacity-60 cursor-not-allowed border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                                    : selectedFromAccount === account.accountNo
                                      ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 cursor-pointer'
                                      : 'border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="radio"
                                    name="fromAccount"
                                    value={account.accountNo}
                                    checked={selectedFromAccount === account.accountNo}
                                    onChange={(e) => !isBusy && setSelectedFromAccount(e.target.value)}
                                    disabled={isBusy}
                                    className="w-4 h-4 text-blue-600 disabled:opacity-50"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium text-gray-800 dark:text-slate-100 text-sm">
                                        {account.accountNo}
                                        {isBusy && (
                                          <span className="ml-2 inline-flex items-center gap-1 text-orange-600 dark:text-orange-400 text-xs font-normal">
                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                            กำลังใช้งาน
                                          </span>
                                        )}
                                      </span>
                                      <span className={`text-sm font-semibold ${account.availableBalance >= totalSelected ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                        ฿{account.availableBalance?.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{account.accountName}</div>
                                    {isBusy && (
                                      <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                                        Admin กำลังโอนเงินจากบัญชีนี้ กรุณาเลือกบัญชีอื่น
                                      </div>
                                    )}
                                    {!isBusy && account.availableBalance < totalSelected && (
                                      <div className="text-xs text-red-500 dark:text-red-400 mt-1">
                                        ⚠️ ยอดเงินไม่เพียงพอ (ต้องการ ฿{totalSelected.toLocaleString()})
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {!loadingAccounts && !needsBankSelection && bankAccounts.length === 0 && withdrawBanks.length > 0 && (
                      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl p-4 text-sm text-center">
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                        ไม่พบบัญชีที่สามารถใช้โอนเงินได้
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-3 text-sm text-gray-500 dark:text-slate-400">
                    <FontAwesomeIcon icon={faMoneyBillWave} className="text-green-500 dark:text-green-400" />
                    <span>ยอดโอนรวม: <strong className="text-gray-800 dark:text-slate-100">฿{totalSelected.toLocaleString()}</strong></span>
                    <span className="ml-2">รายการ: <strong className="text-gray-800 dark:text-slate-100">{selectedPayouts.length} รายการ</strong></span>
                  </div>
                </>
              )}
        </div>
      </UiModal>

      <UiModal
        open={!!confirmGroup}
        onClose={() => !completingBulkId && setConfirmGroup(null)}
        title="ยืนยันโอนสำเร็จ"
        icon={faCheckCircle}
        iconColor="text-green-600"
        size="md"
        scrollable
        footer={
          confirmGroup ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setConfirmGroup(null)} disabled={!!completingBulkId} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                icon={faCheckCircle}
                onClick={handleCompleteUnknownBulk}
                disabled={!!completingBulkId}
                loading={!!completingBulkId}
                className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
              >
                {completingBulkId ? 'กำลังดำเนินการ...' : 'ยืนยันโอนสำเร็จ'}
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {confirmGroup && (
          <>
            <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
                กรุณายืนยันว่าได้ตรวจสอบกับธนาคารแล้ว และ<strong>เงินออกไปเรียบร้อย</strong> การกระทำนี้ไม่สามารถย้อนกลับได้
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">Bulk Order ID</span>
                <code className="text-xs bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 px-2 py-1 rounded text-gray-700 dark:text-slate-200 font-mono max-w-[200px] truncate">
                  {confirmGroup.bulk_order_id}
                </code>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">สถานะธนาคาร</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium">
                  <FontAwesomeIcon icon={faQuestionCircle} className="text-xs" />
                  {confirmGroup.bulk_status}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">บัญชีต้นทาง</span>
                <code className="text-xs bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 px-2 py-1 rounded text-gray-700 dark:text-slate-200 font-mono">
                  {confirmGroup.from_account}
                </code>
              </div>
              <div className="border-t border-gray-200 dark:border-slate-600 pt-2.5 flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">จำนวนรายการ</span>
                <span className="font-semibold text-gray-800 dark:text-slate-100">{confirmGroup.item_count} รายการ</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-slate-400">ยอดรวม</span>
                <span className="font-bold text-lg text-green-700 dark:text-green-400">฿{Number(confirmGroup.total_amount).toLocaleString()}</span>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-slate-600 overflow-hidden max-h-48 overflow-y-auto">
              <div className="px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">รายการที่จะเปลี่ยนเป็น completed</span>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {confirmGroup.items.map((item: any) => (
                  <div key={item.payout_id} className="flex items-center justify-between px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-800 dark:text-slate-100">{item.payout_number}</p>
                      <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{item.bank_name} · {item.bank_account_number}</p>
                    </div>
                    <span className="text-xs font-semibold text-green-700 dark:text-green-400 ml-3 shrink-0">
                      ฿{Number(item.amount).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </UiModal>

      {transferMode === 'scb' && (
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faHistory} className="text-purple-500 dark:text-purple-400" />
              กลุ่มการโอน SCB Anywhere
            </h3>
            <UiButton variant="secondary" icon={faRotate} onClick={loadScbBatchGroups} loading={scbGroupsLoading}>
              Refresh
            </UiButton>
          </div>

          <UiFilterPanel
            dateFrom={scbStartDate}
            dateTo={scbEndDate}
            onDateFromChange={setScbStartDate}
            onDateToChange={setScbEndDate}
            onSearch={handleScbHistorySearch}
            onReset={handleScbHistoryReset}
            loading={scbGroupsLoading}
          >
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                ค้นหา
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ค้นหา Order Number..."
                  value={scbSearchInput}
                  onChange={(e) => setScbSearchInput(e.target.value)}
                  className={SEARCH_INPUT_CLASS}
                />
                {scbSearchInput && (
                  <button
                    type="button"
                    onClick={() => setScbSearchInput('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
            </div>
          </UiFilterPanel>

          <DataTable
            data={pagedScbGroups}
            columns={scbBatchColumns}
            loading={scbGroupsLoading}
            keyExtractor={(group) => group.batch_ref}
            emptyMessage="ยังไม่มีกลุ่มการ Export"
            emptyIcon={<FontAwesomeIcon icon={faHistory} className="text-5xl text-gray-200 dark:text-slate-600" />}
            compact
            hoverable
            rowClassName={(group) => expandedScbGroup === group.batch_ref ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}
            serverPagination={{
              currentPage: scbPage,
              totalItems: scbBatchGroups.length,
              pageSize: scbPageSize,
              pageSizeOptions: [10],
              onPageChange: setScbPage,
            }}
          />

          {expandedScbBatch && (
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                  รายการ Payout ใน Batch {expandedScbBatch.batch_ref} ({expandedScbBatch.payouts.length} รายการ)
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedScbGroup(null)}
                  className="text-xs text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  ปิด
                </button>
              </div>
              <DataTable
                data={expandedScbBatch.payouts}
                columns={scbPayoutItemColumns}
                keyExtractor={(p) => p.payout_id}
                compact
                hoverable
                className="rounded-none shadow-none"
                pagination={false}
              />
            </div>
          )}
        </div>
      )}

      {transferMode === 'ktb_export' && (
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faHistory} className="text-emerald-500 dark:text-emerald-400" />
              กลุ่มการ Export KTB Bulk
            </h3>
            <UiButton variant="secondary" icon={faRotate} onClick={loadKtbBatchGroups} loading={ktbGroupsLoading}>
              Refresh
            </UiButton>
          </div>

          <UiFilterPanel
            dateFrom={ktbStartDate}
            dateTo={ktbEndDate}
            onDateFromChange={setKtbStartDate}
            onDateToChange={setKtbEndDate}
            onSearch={handleKtbHistorySearch}
            onReset={handleKtbHistoryReset}
            loading={ktbGroupsLoading}
          >
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                <FontAwesomeIcon icon={faSearch} className="mr-1" />
                ค้นหา
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="ค้นหา Order Number..."
                  value={ktbSearchInput}
                  onChange={(e) => setKtbSearchInput(e.target.value)}
                  className={SEARCH_INPUT_CLASS}
                />
                {ktbSearchInput && (
                  <button
                    type="button"
                    onClick={() => setKtbSearchInput('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                )}
              </div>
            </div>
          </UiFilterPanel>

          <DataTable
            data={pagedKtbGroups}
            columns={ktbBatchColumns}
            loading={ktbGroupsLoading}
            keyExtractor={(group) => group.batch_ref}
            emptyMessage="ยังไม่มีกลุ่มการ Export"
            emptyIcon={<FontAwesomeIcon icon={faHistory} className="text-5xl text-gray-200 dark:text-slate-600" />}
            compact
            hoverable
            rowClassName={(group) => expandedKtbGroup === group.batch_ref ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''}
            serverPagination={{
              currentPage: ktbPage,
              totalItems: ktbBatchGroups.length,
              pageSize: ktbPageSize,
              pageSizeOptions: [10],
              onPageChange: setKtbPage,
            }}
          />

          {expandedKtbBatch && (
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 dark:text-slate-300">
                  รายการ Payout ใน Batch {expandedKtbBatch.batch_ref} ({expandedKtbBatch.payouts.length} รายการ)
                </span>
                <button
                  type="button"
                  onClick={() => setExpandedKtbGroup(null)}
                  className="text-xs text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="mr-1" />
                  ปิด
                </button>
              </div>
              <DataTable
                data={expandedKtbBatch.payouts}
                columns={scbPayoutItemColumns}
                keyExtractor={(p) => p.payout_id}
                compact
                hoverable
                className="rounded-none shadow-none"
                pagination={false}
              />
            </div>
          )}
        </div>
      )}

      <UiModal
        open={showScbExportModal}
        onClose={() => !scbExporting && setShowScbExportModal(false)}
        title="ยืนยันการ Export ไฟล์ SCB Anywhere"
        icon={faHistory}
        iconColor="text-purple-500"
        size="xl"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowScbExportModal(false)} disabled={scbExporting} className="flex-1 justify-center">
              ปิด
            </UiButton>
            <UiButton
              icon={faHistory}
              onClick={handleScbExportConfirm}
              disabled={scbExporting || !scbBanksId}
              loading={scbExporting}
              className="flex-1 justify-center !bg-purple-600 hover:!bg-purple-700"
            >
              {scbExporting ? 'กำลัง Export...' : 'ยืนยัน Export'}
            </UiButton>
          </div>
        }
      >
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${scbFilter === 'scb' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : scbFilter === 'other' ? 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'}`}>
            {scbFilter === 'scb' ? 'โอนไปยัง SCB' : scbFilter === 'other' ? 'โอนไปยังธนาคารอื่นๆ' : 'ทั้งหมด (SCB + ธนาคารอื่นๆ)'}
          </span>
          <span className="text-sm text-gray-500 dark:text-slate-400">{selectedPayouts.length} รายการ</span>
        </div>

        <div>
          <label className={LABEL_CLASS}>
            บัญชีต้นทาง SCB <span className="text-red-500">*</span>
          </label>
          {scbModalBanksLoading ? (
            <p className="text-sm text-gray-400 dark:text-slate-500 flex items-center gap-2 py-2">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> กำลังโหลด...
            </p>
          ) : (
            <select
              value={scbBanksId}
              onChange={e => {
                const bank = scbModalBanks.find(b => b.id === e.target.value);
                setScbBanksId(e.target.value);
                setScbFromAccount(bank?.account_number || '');
              }}
              disabled={scbExporting}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">— เลือกบัญชีต้นทาง —</option>
              {scbModalBanks.map(bank => (
                <option key={bank.id} value={bank.id}>
                  {bank.name} ({bank.account_number})
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">#</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขที่ Payout</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">Merchant</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ธนาคาร / บัญชี</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ยอดสุทธิ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {selectedPayouts.map((p, i) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-2 text-gray-400 dark:text-slate-500 text-xs">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-gray-800 dark:text-slate-100">{p.payout_number}</td>
                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-slate-400">{p.merchant?.name}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 dark:text-slate-300">
                    {p.bank_name} — <code className="bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 rounded">{p.bank_account_number}</code>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-green-700 dark:text-green-400">
                    ฿{Number(p.net_amount).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-600">
                <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200">รวมทั้งหมด</td>
                <td className="px-4 py-2 text-right font-bold text-green-700 dark:text-green-400">
                  ฿{selectedPayouts.reduce((s, p) => s + Number(p.net_amount), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
          <span>สถานะจะปรับเป็น <strong>Approved</strong> ทันที และระบบจะส่ง Webhook แจ้งร้านค้าทุกรายการ</span>
        </div>
      </UiModal>

      {/* SCB Batch Complete Confirm Modal */}
      {showScbCompleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !completingScbBatch && setShowScbCompleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}>

            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                </span>
                ยืนยันปรับสถานะ Completed
              </h2>
              <button onClick={() => setShowScbCompleteConfirm(null)} disabled={completingScbBatch}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 disabled:opacity-40">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {(() => {
                const group = scbBatchGroups.find(g => g.batch_ref === showScbCompleteConfirm);
                return group ? (
                  <>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Batch Ref</span>
                        <code className="text-gray-700">{group.batch_ref}</code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">ประเภท</span>
                        <span className="text-gray-700">{group.filter_type === 'scb' ? 'โอนไปยัง SCB' : 'ธนาคารอื่นๆ'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">จำนวนรายการ</span>
                        <span className="font-semibold text-gray-800">{group.item_count} รายการ</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">ยอดรวม</span>
                        <span className="font-bold text-green-700">฿{Number(group.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
                      <span>ระบบจะส่ง Webhook <strong>payout.completed</strong> ให้ร้านค้าทุกรายการในชุดนี้ทันที</span>
                    </div>
                  </>
                ) : null;
              })()}
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowScbCompleteConfirm(null)} disabled={completingScbBatch}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors disabled:opacity-50">
                ยกเลิก
              </button>
              <button onClick={handleScbBatchComplete} disabled={completingScbBatch}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {completingScbBatch ? (
                  <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> กำลังดำเนินการ...</>
                ) : (
                  <><FontAwesomeIcon icon={faCheckCircle} /> ยืนยัน</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KTB Export Confirm Modal */}
      <UiModal
        open={showKtbExportModal}
        onClose={() => !ktbExporting && setShowKtbExportModal(false)}
        title="ยืนยันการ Export ไฟล์ KTB Bulk"
        icon={faHistory}
        iconColor="text-emerald-500"
        size="xl"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowKtbExportModal(false)} disabled={ktbExporting} className="flex-1 justify-center">
              ปิด
            </UiButton>
            <UiButton
              icon={faHistory}
              onClick={handleKtbExportConfirm}
              disabled={ktbExporting || !ktbBanksId}
              loading={ktbExporting}
              className="flex-1 justify-center !bg-emerald-600 hover:!bg-emerald-700"
            >
              {ktbExporting ? 'กำลัง Export...' : 'ยืนยัน Export'}
            </UiButton>
          </div>
        }
      >
        <div className="flex items-center gap-3">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
            KTB Bulk Transfer
          </span>
          <span className="text-sm text-gray-500 dark:text-slate-400">{selectedPayouts.length} รายการ</span>
        </div>

        <div>
          <label className={LABEL_CLASS}>
            บัญชีต้นทาง KTB <span className="text-red-500">*</span>
          </label>
          {ktbModalBanksLoading ? (
            <p className="text-sm text-gray-400 dark:text-slate-500 flex items-center gap-2 py-2">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> กำลังโหลด...
            </p>
          ) : (
            <select
              value={ktbBanksId}
              onChange={e => {
                const bank = ktbModalBanks.find(b => b.id === e.target.value);
                setKtbBanksId(e.target.value);
                setKtbFromAccount(bank?.account_number || '');
              }}
              disabled={ktbExporting}
              className={FILTER_SELECT_CLASS}
            >
              <option value="">— เลือกบัญชีต้นทาง —</option>
              {ktbModalBanks.map(bank => (
                <option key={bank.id} value={bank.id}>
                  {bank.name} ({bank.account_number})
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-600">
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">#</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">เลขที่ Payout</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">Merchant</th>
                <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ธนาคาร / บัญชี</th>
                <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400">ยอดสุทธิ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {selectedPayouts.map((p, i) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                  <td className="px-4 py-2 text-gray-400 dark:text-slate-500 text-xs">{i + 1}</td>
                  <td className="px-4 py-2 font-medium text-gray-800 dark:text-slate-100">{p.payout_number}</td>
                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-slate-400">{p.merchant?.name}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 dark:text-slate-300">
                    {p.bank_name} — <code className="bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200 px-1 rounded">{p.bank_account_number}</code>
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-green-700 dark:text-green-400">
                    ฿{Number(p.net_amount).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 dark:bg-slate-700/50 border-t border-gray-200 dark:border-slate-600">
                <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200">รวมทั้งหมด</td>
                <td className="px-4 py-2 text-right font-bold text-green-700 dark:text-green-400">
                  ฿{selectedPayouts.reduce((s, p) => s + Number(p.net_amount), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
          <span>สถานะจะปรับเป็น <strong>Approved</strong> และระบบจะสร้างไฟล์ให้ดาวน์โหลดทันที พร้อมส่ง Webhook แจ้งร้านค้าทุกรายการ</span>
        </div>
      </UiModal>

      {/* KTB Batch Complete Confirm Modal */}
      {showKtbCompleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !completingKtbBatch && setShowKtbCompleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}>

            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                </span>
                ยืนยันปรับสถานะ Completed
              </h2>
              <button onClick={() => setShowKtbCompleteConfirm(null)} disabled={completingKtbBatch}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 disabled:opacity-40">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {(() => {
                const group = ktbBatchGroups.find(g => g.batch_ref === showKtbCompleteConfirm);
                return group ? (
                  <>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Batch Ref</span>
                        <code className="text-gray-700">{group.batch_ref}</code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">บัญชีต้นทาง</span>
                        <code className="text-gray-700">{group.from_account || '-'}</code>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">จำนวนรายการ</span>
                        <span className="font-semibold text-gray-800">{group.item_count} รายการ</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">ยอดรวม</span>
                        <span className="font-bold text-green-700">฿{Number(group.total_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
                      <span>ระบบจะส่ง Webhook <strong>payout.completed</strong> ให้ร้านค้าทุกรายการในชุดนี้ทันที</span>
                    </div>
                  </>
                ) : null;
              })()}
            </div>

            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowKtbCompleteConfirm(null)} disabled={completingKtbBatch}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors disabled:opacity-50">
                ยกเลิก
              </button>
              <button onClick={handleKtbBatchComplete} disabled={completingKtbBatch}
                className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {completingKtbBatch ? (
                  <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> กำลังดำเนินการ...</>
                ) : (
                  <><FontAwesomeIcon icon={faCheckCircle} /> ยืนยัน</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== KTB อัตโนมัติ (บอท): เลือกบัญชีต้นทางจาก KTB ===== */}
      <UiModal
        open={showAutoAccountsModal}
        onClose={() => setShowAutoAccountsModal(false)}
        title="เลือกบัญชีต้นทางจาก KTB Business"
        icon={faUniversity}
        iconColor="text-orange-500"
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowAutoAccountsModal(false)} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              icon={faCheck}
              onClick={() => {
                setAutoForm(f => ({ ...f, fromAccount: autoAccountPick }));
                setShowAutoAccountsModal(false);
                toast.info('ใส่เลขบัญชีในฟอร์มแล้ว — กด "บันทึกการตั้งค่า" เพื่อยืนยัน');
              }}
              disabled={!autoAccountPick}
              className="flex-1 justify-center"
            >
              ใช้บัญชีนี้
            </UiButton>
          </div>
        }
      >
        <p className="text-xs text-gray-500 dark:text-slate-400">บัญชีที่ KTB Business คืนมาสำหรับธนาคารที่เลือก — เลือกบัญชีที่บอทจะใช้โอนออก</p>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {autoAccounts.map(acc => (
            <label
              key={acc.accountRefId || acc.accountNo}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${autoAccountPick === acc.accountNo
                ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
                : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}
            >
              <input
                type="radio"
                name="auto-from-account"
                value={acc.accountNo}
                checked={autoAccountPick === acc.accountNo}
                onChange={() => setAutoAccountPick(acc.accountNo)}
                className="w-4 h-4"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 dark:text-slate-100 font-mono">{acc.accountNo}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{acc.accountName}</p>
              </div>
              <span className="text-xs font-semibold text-green-700 dark:text-green-400 whitespace-nowrap">
                {formatBaht(acc.availableBalance)}
              </span>
            </label>
          ))}
        </div>
      </UiModal>

      {/* ===== KTB อัตโนมัติ (บอท): ตรวจกับธนาคาร (reconcile) ===== */}
      <UiModal
        open={!!reconcileBatch}
        onClose={() => { if (!reconcileSubmitting) { setReconcileBatch(null); setReconcileResult(null); } }}
        title={`ตรวจกับธนาคาร — ${reconcileBatch?.batch_ref || ''}`}
        icon={faQuestionCircle}
        iconColor="text-red-500"
        size="xl"
        scrollable
        closeOnBackdrop={false}
        footer={reconcileBatch ? (
          <div className="space-y-3">
            <UiInput
              label="หมายเหตุ (บันทึกลง log ของทุก payout ในชุด)"
              value={reconcileNote}
              onChange={(e) => setReconcileNote(e.target.value)}
              placeholder="เช่น ตรวจใน KTB Business แล้ว เห็นรายการโอนครบ 10/10"
              disabled={reconcileSubmitting}
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <UiButton
                variant="secondary"
                onClick={() => { setReconcileBatch(null); setReconcileResult(null); }}
                disabled={reconcileSubmitting}
                className="justify-center"
              >
                ปิด
              </UiButton>
              <UiButton
                variant="secondary"
                icon={faRotate}
                onClick={() => reconcileBatch && openReconcileModal(reconcileBatch)}
                loading={reconcileLoading}
                disabled={reconcileSubmitting}
                className="justify-center"
              >
                ตรวจอีกครั้ง
              </UiButton>
              <div className="flex-1" />
              <UiButton
                variant="danger"
                icon={faTimesCircle}
                onClick={() => confirmReconcile('fail')}
                disabled={reconcileLoading || reconcileSubmitting || !canApprove || !reconcileCanDecide}
                className="justify-center"
              >
                ยืนยันว่าเงินไม่ออก (คืน pending)
              </UiButton>
              <UiButton
                icon={faCheckCircle}
                onClick={() => confirmReconcile('complete')}
                loading={reconcileSubmitting}
                disabled={reconcileLoading || !canApprove || !reconcileCanDecide}
                className="justify-center !bg-green-600 hover:!bg-green-700"
              >
                ยืนยันว่าโอนสำเร็จแล้ว (ปิดเป็น completed)
              </UiButton>
            </div>
          </div>
        ) : undefined}
      >
        {reconcileBatch && (
          <>
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mt-0.5 shrink-0" />
              <span>
                การตัดสินใจนี้กำหนดว่า payout ในชุดนี้จะ<strong>ถูกจ่ายซ้ำหรือไม่</strong> — ปิดเป็น completed = ถือว่าเงินออกแล้ว (จะไม่โอนอีก) ·
                คืน pending = ถือว่าเงินไม่ออก บอท/แอดมินจะหยิบไปโอนใหม่{' '}
                <strong>กรุณาเปิดระบบ KTB Business ตรวจรายการโอนจริงก่อนกดยืนยันทุกครั้ง</strong>
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">สถานะ batch</p>
                <StatusBadge status={reconcileBatch.status} statusMap={BATCH_STATUS_MAP} pulse={reconcileBatch.status === 'unknown'} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">แหล่ง / จำนวน</p>
                <p className="font-semibold text-gray-800 dark:text-slate-100">{reconcileBatch.source === 'auto' ? 'บอท' : 'กดเอง'} · {reconcileBatch.payout_count} รายการ</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">ยอดรวม</p>
                <p className="font-bold text-green-700 dark:text-green-400">{formatBaht(reconcileBatch.total_amount)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">ส่งเมื่อ</p>
                <p className="text-gray-700 dark:text-slate-300">{formatDateTime(reconcileBatch.submitted_at)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">Bulk Order ID / Ref No</p>
                <code className="text-xs font-mono text-purple-700 dark:text-purple-300 break-all">{reconcileBatch.bulk_order_id || '-'}</code>
                <span className="text-xs text-gray-400 dark:text-slate-500"> / {reconcileBatch.package_ref_no || '-'}</span>
              </div>
              <div className="col-span-2">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">บัญชีต้นทาง</p>
                <code className="text-xs font-mono text-gray-700 dark:text-slate-200">{reconcileBatch.from_account || '-'}</code>
              </div>
              {reconcileBatch.error_message && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-slate-500">ข้อผิดพลาดที่บันทึกไว้</p>
                  <p className="text-xs text-red-600 dark:text-red-400 break-words">{reconcileBatch.error_message}</p>
                </div>
              )}
            </div>

            {reconcileLoading ? (
              <div className="py-8 text-center text-gray-400 dark:text-slate-500 text-sm">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                กำลังตรวจกับ KTB server และ KTB Business...
              </div>
            ) : reconcileResult && (
              <div className="space-y-3">
                <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-3">
                  <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">1) KTB server (ค้นตาม batch_ref)</p>
                  {!reconcileResult.server || reconcileResult.server.found === false ? (
                    <p className="text-sm text-gray-500 dark:text-slate-400">ไม่พบข้อมูลใน server — server อาจไม่ได้รับคำสั่ง หรือเป็น server รุ่นเก่า (ห้ามตีความว่าเงินไม่ออก)</p>
                  ) : reconcileResult.server.error ? (
                    <p className="text-sm text-red-600 dark:text-red-400 break-words">ติดต่อ server ไม่ได้: {reconcileResult.server.error}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500">state</p>
                        <p className="font-semibold text-gray-800 dark:text-slate-100">{reconcileResult.server.state || '-'}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500">phase</p>
                        <p className="font-semibold text-gray-800 dark:text-slate-100">{reconcileResult.server.phase || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[11px] text-gray-400 dark:text-slate-500">bulk_order_id</p>
                        <code className="text-xs font-mono text-purple-700 dark:text-purple-300 break-all">{reconcileResult.server.bulk_order_id || '-'}</code>
                      </div>
                      {reconcileResult.server.error && (
                        <p className="col-span-2 md:col-span-4 text-xs text-red-600 dark:text-red-400 break-words">{reconcileResult.server.error}</p>
                      )}
                      {reconcileResult.server.result && (
                        <pre className="col-span-2 md:col-span-4 text-[11px] bg-gray-50 dark:bg-slate-900/50 rounded-lg p-2 overflow-x-auto max-h-40 text-gray-600 dark:text-slate-300">
                          {JSON.stringify(reconcileResult.server.result, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-slate-700 p-3">
                  <p className="text-xs font-semibold text-gray-600 dark:text-slate-300 mb-2">2) KTB Business (สถานะ bulk ตาม bulk_order_id)</p>
                  {!reconcileResult.ktb ? (
                    <p className="text-sm text-gray-500 dark:text-slate-400">ยังไม่มี bulk_order_id — ยังไม่ได้เช็คกับ KTB Business (คำสั่งอาจไปไม่ถึงธนาคาร)</p>
                  ) : reconcileResult.ktb.error ? (
                    <p className="text-sm text-red-600 dark:text-red-400 break-words">เช็คสถานะไม่ได้: {reconcileResult.ktb.error}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-gray-500 dark:text-slate-400">bulk_status:</span>
                        {getBulkStatusBadge(reconcileResult.ktb.bulk_status || '-')}
                        {reconcileResult.ktb.package_ref_no && <span className="text-xs text-gray-400 dark:text-slate-500">Ref {reconcileResult.ktb.package_ref_no}</span>}
                      </div>
                      {!!reconcileResult.ktb.items?.length && (
                        <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-slate-700">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400">
                                <th className="px-3 py-2 text-left font-medium">ปลายทาง</th>
                                <th className="px-3 py-2 text-left font-medium">ชื่อบัญชี</th>
                                <th className="px-3 py-2 text-right font-medium">ยอด</th>
                                <th className="px-3 py-2 text-center font-medium">สถานะ KTB</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                              {reconcileResult.ktb.items.map((it, idx) => (
                                <tr key={it.bulkItemId || idx}>
                                  <td className="px-3 py-2">
                                    <p className="text-gray-700 dark:text-slate-300">{it.payeeBankName || it.payeeBankCode || '-'}</p>
                                    <code className="font-mono text-gray-500 dark:text-slate-400">{it.payeeAccountNo}</code>
                                  </td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">{it.payeeAccountName || '-'}</td>
                                  <td className="px-3 py-2 text-right font-semibold text-gray-800 dark:text-slate-100">{formatBaht(it.amount)}</td>
                                  <td className="px-3 py-2 text-center">{getBulkStatusBadge(it.status || '-')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {reconcileResult.auto && (
                  reconcileResult.auto.resolved ? (
                    <div className="flex items-start gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-4 py-3 text-sm text-green-700 dark:text-green-300">
                      <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 shrink-0" />
                      <span>ระบบสรุปผลให้อัตโนมัติจากข้อมูล server/สถานะสด KTB แล้ว: <strong>{reconcileResult.auto.action}</strong>{reconcileResult.auto.ktb_status ? ` (KTB: ${reconcileResult.auto.ktb_status})` : ''} — ไม่ต้องตัดสินใจเอง กด "ปิด" แล้วดูสถานะใหม่ในตาราง</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-xl px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                      <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 shrink-0" />
                      <span>
                        ระบบสรุปอัตโนมัติไม่ได้ (ข้อมูลไม่พอชี้ขาด)
                        {reconcileResult.auto.ktb_status ? <> — สถานะสดจาก KTB: <strong>{reconcileResult.auto.ktb_status}</strong>{reconcileResult.auto.ktb_status === 'DRAFT' ? ' (ยังไม่ SUBMITTED — บอทจะเช็คซ้ำให้เองทุกรอบ ถ้าค้างนานให้ตรวจใน KTB Business)' : ''}</> : ' — ต้องตรวจใน KTB Business แล้วตัดสินใจเองด้านล่าง'}
                      </span>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}
      </UiModal>

      {/* ===== KTB อัตโนมัติ (บอท): confirm ร่วม ===== */}
      <UiModal
        open={!!autoConfirm}
        onClose={() => { if (!autoConfirmBusy) setAutoConfirm(null); }}
        title={autoConfirm?.title || ''}
        icon={autoConfirm?.danger ? faExclamationTriangle : faQuestionCircle}
        iconColor={autoConfirm?.danger ? 'text-red-500' : 'text-amber-500'}
        size="md"
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setAutoConfirm(null)} disabled={autoConfirmBusy} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              variant={autoConfirm?.danger ? 'danger' : 'primary'}
              icon={autoConfirm?.danger ? faTimesCircle : faCheck}
              onClick={runAutoConfirm}
              loading={autoConfirmBusy}
              className="flex-1 justify-center"
            >
              {autoConfirm?.confirmLabel || 'ยืนยัน'}
            </UiButton>
          </div>
        }
      >
        <p className="text-sm leading-relaxed">{autoConfirm?.message}</p>
      </UiModal>

    </div>
  );
};

export default BulkTransferPage;
